import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  Plus, Route, MapPin, Clock, Fuel, Navigation,
  GripVertical, Play, CheckCircle, Trash2, RefreshCw,
  Warehouse, ChevronDown, ChevronUp, Edit, X,
  UserPlus, Phone, Package, MessageSquare, Send,
  Users, AlertCircle, Printer, FileText,
  History, Star, DollarSign, Wallet, BadgeCheck, FileSpreadsheet, Shuffle, Receipt, CalendarClock,
} from "lucide-react";

// Leaflet imports
import { MapContainer, TileLayer, Marker, Popup, Polyline } from "react-leaflet";

// Split-off utils + dialogs (extracted to shrink this file)
import { depotIcon, deliveryIcon, deliveryIconPending, FitBounds, STATUT_OPTIONS } from "./tournees-utils";
import FraisDialog from "./tournees/FraisDialog";
import MergeDialog from "./tournees/MergeDialog";
import EnveloppeDialog from "./tournees/EnveloppeDialog";
import ChargementDialog from "./tournees/ChargementDialog";

const Tournees = () => {
  const { token } = useAuth();
  const [tournees, setTournees] = useState([]);
  const [livraisons, setLivraisons] = useState([]);
  const [selectedTournee, setSelectedTournee] = useState(null);
  const [tourneeDetails, setTourneeDetails] = useState(null);
  const [smsTemplateBody, setSmsTemplateBody] = useState("");
  const [smsAvisTemplateBody, setSmsAvisTemplateBody] = useState("");
  const [reportDialog, setReportDialog] = useState(null); // livraison à reporter
  const [reportForm, setReportForm] = useState({ raison: "", note: "", date_souhaitee: "", creer_prochaine: true, tournee_cible_id: "none" });
  const [reportSaving, setReportSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [splitDialogOpen, setSplitDialogOpen] = useState(false);
  const [splitCap, setSplitCap] = useState(100);
  const [splitStrategie, setSplitStrategie] = useState("geo");
  const [splitDeleteOriginal, setSplitDeleteOriginal] = useState(false);
  const [splitting, setSplitting] = useState(false);
  const submitSplit = async () => {
    if (!tourneeDetails?.tournee?.id) return;
    setSplitting(true);
    try {
      const res = await axios.post(`${API}/tournees/${tourneeDetails.tournee.id}/split`, {
        capacite_max: splitCap,
        strategie: splitStrategie,
        delete_original: splitDeleteOriginal,
      }, { headers });
      const n = res.data.nb_tournees || 0;
      toast.success(`✅ ${n} tournée(s) créée(s) (${res.data.total_chaises} chaises réparties)`, { duration: 8000 });
      setSplitDialogOpen(false);
      fetchData();
      setSelectedTournee(null);
      setTourneeDetails(null);
    } catch (err) { toast.error(err?.response?.data?.detail || "Erreur split"); }
    finally { setSplitting(false); }
  };
  const [optimizing, setOptimizing] = useState(false);
  const [selectedLivraisons, setSelectedLivraisons] = useState([]);
  const [draggedItem, setDraggedItem] = useState(null);
  const [editLivraisonDialog, setEditLivraisonDialog] = useState(false);
  const [editingLivraison, setEditingLivraison] = useState(null);
  const [addLivraisonDialog, setAddLivraisonDialog] = useState(false);
  const [allLivraisons, setAllLivraisons] = useState([]);
  const [smsDialogOpen, setSmsDialogOpen] = useState(false);
  const [smsStartTime, setSmsStartTime] = useState("09:00");
  const [smsDuration, setSmsDuration] = useState(17);
  const [smsMessages, setSmsMessages] = useState([]);
  const [smsDate, setSmsDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [chargementDialogOpen, setChargementDialogOpen] = useState(false);
  const [pendingStatutChange, setPendingStatutChange] = useState(null);
  
  // CRM Clients for adding to tournee
  const [clients, setClients] = useState([]);
  const [addDialogTab, setAddDialogTab] = useState("livraisons"); // "livraisons" or "clients"
  const [clientSearchTerm, setClientSearchTerm] = useState("");
  const [creatingFromClient, setCreatingFromClient] = useState(false);
  const [newLivraisonFromClient, setNewLivraisonFromClient] = useState({
    details_commande: "",
    quantite: 1,
    prix: null,
    notes: "",
  });
  
  // For creation dialog: track livraisons created from clients
  const [createDialogTab, setCreateDialogTab] = useState("livraisons");
  const [createClientSearch, setCreateClientSearch] = useState("");
  const [pendingClientLivraisons, setPendingClientLivraisons] = useState([]); // Clients to create livraisons for
  
  // Marketing - liens d'avis pour SMS
  const [liensAvis, setLiensAvis] = useState([]);
  const [selectedLienAvis, setSelectedLienAvis] = useState("");
  const [smsType, setSmsType] = useState("livraison"); // "livraison" or "avis"
  
  // Pour SMS avis avec choix par client
  const [avisClients, setAvisClients] = useState([]); // [{id, nom, telephone, lien_avis_id}]
  const [newAvisClient, setNewAvisClient] = useState({ nom: "", telephone: "" });

  // Tournée historique
  const [historiqueDialogOpen, setHistoriqueDialogOpen] = useState(false);
  const [historiqueForm, setHistoriqueForm] = useState({
    nom: "",
    date: new Date(Date.now() - 86400000).toISOString().slice(0, 10), // Hier par défaut
    deduire_stock: false,
    enveloppe_montant: "",
    enveloppe_nb_chaises: "",
  });
  const [historiqueClients, setHistoriqueClients] = useState([
    { id: Date.now(), nom: "", telephone: "", details_commande: "", quantite: 1, prix: "" },
  ]);

  // Enveloppe dialog
  const [enveloppeDialogOpen, setEnveloppeDialogOpen] = useState(false);
  const [enveloppeDialogTourneeId, setEnveloppeDialogTourneeId] = useState(null);
  const [enveloppeForm, setEnveloppeForm] = useState({
    montant: "",
    nb_chaises: "",
    remise: false,
    date_remise: "",
  });

  // Frais dialog (essence, adblue, etc.)
  const [fraisDialogOpen, setFraisDialogOpen] = useState(false);
  const [fraisDialogTourneeId, setFraisDialogTourneeId] = useState(null);
  const [fraisForm, setFraisForm] = useState({ type: "essence", montant: "", note: "" });
  const [fraisSubmitting, setFraisSubmitting] = useState(false);

  // Merge selection
  const [selectedTourneesForMerge, setSelectedTourneesForMerge] = useState(new Set());
  // Filtre & export bulk (Phase 3)
  const [tourneeDateFilter, setTourneeDateFilter] = useState({ debut: "", fin: "" });
  const [bulkExporting, setBulkExporting] = useState(false);
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [mergePrimaryId, setMergePrimaryId] = useState("");
  const [mergeDate, setMergeDate] = useState("");
  const [mergeSubmitting, setMergeSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    nom: "",
    date: new Date().toISOString().slice(0, 10),
    point_depart_adresse: "",
  });

  const [editForm, setEditForm] = useState({
    client_nom: "",
    client_telephone: "",
    adresse: "",
    ville: "",
    code_postal: "",
    details_commande: "",
    quantite: 1,
    notes: "",
  });

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = async () => {
    try {
      const [tourneesRes, livraisonsRes, allLivraisonsRes, clientsRes, liensAvisRes] = await Promise.all([
        axios.get(`${API}/tournees`, { headers }),
        axios.get(`${API}/livraisons?statut=en_attente`, { headers }),
        axios.get(`${API}/livraisons`, { headers }),
        axios.get(`${API}/clients`, { headers }),
        axios.get(`${API}/marketing/liens-avis`, { headers }),
      ]);
      setTournees(tourneesRes.data);
      setLivraisons(livraisonsRes.data);
      setAllLivraisons(allLivraisonsRes.data);
      setClients(clientsRes.data);
      setLiensAvis(liensAvisRes.data.filter(l => l.actif));
    } catch (error) {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  const fetchTourneeDetails = async (tourneeId) => {
    try {
      const response = await axios.get(`${API}/tournees/${tourneeId}/livraisons`, { headers });
      setTourneeDetails(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement de la tournée");
    }
  };

  // Edit livraison from tournee
  const openReport = (l) => {
    setReportForm({ raison: "", note: "", date_souhaitee: "", creer_prochaine: true, tournee_cible_id: "none" });
    setReportDialog(l);
  };

  const toggleConfirmation = async (l) => {
    const next = l.confirmation_presence === "confirme" ? "en_attente" : "confirme";
    try {
      await axios.put(`${API}/livraisons/${l.id}/confirmation`, { confirmation: next }, { headers });
      toast.success(next === "confirme" ? "Présence confirmée ✅" : "Confirmation retirée");
      if (selectedTournee) fetchTourneeDetails(selectedTournee);
    } catch { toast.error("Erreur"); }
  };

  const submitReport = async () => {
    setReportSaving(true);
    try {
      const res = await axios.post(`${API}/livraisons/${reportDialog.id}/reporter`, {
        raison: reportForm.raison,
        note: reportForm.note,
        date_souhaitee: reportForm.date_souhaitee || null,
        creer_prochaine: reportForm.creer_prochaine,
        tournee_cible_id: reportForm.tournee_cible_id !== "none" ? reportForm.tournee_cible_id : null,
      }, { headers });
      toast.success(res.data.nouveau_numero ? `Reportée ✅ Nouvelle commande #${res.data.nouveau_numero} créée` : "Livraison reportée");
      setReportDialog(null);
      fetchData();
      if (selectedTournee) fetchTourneeDetails(selectedTournee);
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setReportSaving(false); }
  };

  const openEditLivraison = (livraison) => {
    setEditingLivraison(livraison);
    setEditForm({
      client_nom: livraison.client_nom,
      client_telephone: livraison.client_telephone || "",
      adresse: livraison.adresse,
      ville: livraison.ville,
      code_postal: livraison.code_postal || "",
      details_commande: livraison.details_commande || "",
      quantite: livraison.quantite,
      notes: livraison.notes || "",
    });
    setEditLivraisonDialog(true);
  };

  const handleEditLivraison = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/livraisons/${editingLivraison.id}`, editForm, { headers });
      toast.success("Livraison mise à jour !");
      setEditLivraisonDialog(false);
      setEditingLivraison(null);
      if (selectedTournee) {
        fetchTourneeDetails(selectedTournee);
      }
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  // Remove livraison from tournee
  const handleRemoveFromTournee = async (livraisonId) => {
    if (!window.confirm("Retirer cette livraison de la tournée ?")) return;
    
    try {
      // Update livraison to remove tournee reference
      await axios.put(`${API}/livraisons/${livraisonId}`, {
        tournee_id: null,
        ordre_dans_tournee: null
      }, { headers });
      
      // Update tournee to remove livraison
      if (tourneeDetails) {
        const newLivraisonsIds = tourneeDetails.tournee.livraisons_ids.filter(id => id !== livraisonId);
        const newOrdre = tourneeDetails.tournee.ordre_livraisons.filter(id => id !== livraisonId);
        
        await axios.put(`${API}/tournees/${selectedTournee}`, {
          livraisons_ids: newLivraisonsIds,
          ordre_livraisons: newOrdre
        }, { headers });
      }
      
      toast.success("Livraison retirée de la tournée");
      fetchTourneeDetails(selectedTournee);
      fetchData();
    } catch (error) {
      toast.error("Erreur lors du retrait");
    }
  };

  // Add livraison to existing tournee
  const handleAddToTournee = async (livraisonId) => {
    if (!tourneeDetails) return;
    
    try {
      const newLivraisonsIds = [...tourneeDetails.tournee.livraisons_ids, livraisonId];
      const newOrdre = [...tourneeDetails.tournee.ordre_livraisons, livraisonId];
      
      await axios.put(`${API}/tournees/${selectedTournee}`, {
        livraisons_ids: newLivraisonsIds,
        ordre_livraisons: newOrdre
      }, { headers });
      
      // Update livraison with tournee reference
      await axios.put(`${API}/livraisons/${livraisonId}`, {
        tournee_id: selectedTournee,
        ordre_dans_tournee: newOrdre.length
      }, { headers });
      
      toast.success("Livraison ajoutée à la tournée");
      setAddLivraisonDialog(false);
      fetchTourneeDetails(selectedTournee);
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de l'ajout");
    }
  };

  // Create livraison from CRM client and add to tournee
  const handleAddClientToTournee = async (client) => {
    if (!tourneeDetails || !client.adresse || !client.ville) {
      toast.error("Le client doit avoir une adresse complète");
      return;
    }
    
    setCreatingFromClient(true);
    try {
      // Step 1: Create the livraison
      const livraisonData = {
        client_nom: client.nom,
        client_telephone: client.telephone || "",
        adresse: client.adresse,
        ville: client.ville,
        code_postal: client.code_postal || "",
        pays: "Belgique",
        details_commande: newLivraisonFromClient.details_commande || "",
        quantite: newLivraisonFromClient.quantite || 1,
        prix: newLivraisonFromClient.prix || null,
        notes: newLivraisonFromClient.notes || "",
        date_livraison_prevue: tourneeDetails.tournee.date,
      };
      
      const createRes = await axios.post(`${API}/livraisons`, livraisonData, { headers });
      const newLivraisonId = createRes.data.id;
      
      // Step 2: Geocode the livraison
      await axios.post(`${API}/livraisons/geocode`, { ids: [newLivraisonId] }, { headers });
      
      // Step 3: Add to the tournee
      const newLivraisonsIds = [...tourneeDetails.tournee.livraisons_ids, newLivraisonId];
      const newOrdre = [...tourneeDetails.tournee.ordre_livraisons, newLivraisonId];
      
      await axios.put(`${API}/tournees/${selectedTournee}`, {
        livraisons_ids: newLivraisonsIds,
        ordre_livraisons: newOrdre
      }, { headers });
      
      await axios.put(`${API}/livraisons/${newLivraisonId}`, {
        tournee_id: selectedTournee,
        ordre_dans_tournee: newOrdre.length
      }, { headers });
      
      toast.success(`Client "${client.nom}" ajouté à la tournée`);
      setAddLivraisonDialog(false);
      setNewLivraisonFromClient({ details_commande: "", quantite: 1, prix: null, notes: "" });
      fetchTourneeDetails(selectedTournee);
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de l'ajout du client");
      console.error(error);
    } finally {
      setCreatingFromClient(false);
    }
  };

  // Mark livraison as delivered
  const handleMarkDelivered = async (livraisonId) => {
    try {
      await axios.put(`${API}/livraisons/${livraisonId}`, { statut: "livree" }, { headers });
      toast.success("Livraison marquée comme livrée !");
      fetchTourneeDetails(selectedTournee);
    } catch (error) {
      toast.error("Erreur");
    }
  };

  // Undo delivery - revert to en_cours
  const handleUndoDelivery = async (livraisonId) => {
    try {
      await axios.put(`${API}/livraisons/${livraisonId}`, { statut: "en_cours" }, { headers });
      toast.success("Livraison remise en cours");
      fetchTourneeDetails(selectedTournee);
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const handleCreateTournee = async (e) => {
    e.preventDefault();

    if (!formData.nom || (selectedLivraisons.length === 0 && pendingClientLivraisons.length === 0)) {
      toast.error("Veuillez remplir le nom et sélectionner au moins un arrêt");
      return;
    }

    try {
      let allLivraisonIds = [...selectedLivraisons];
      
      // If there are clients to convert to livraisons
      if (pendingClientLivraisons.length > 0) {
        toast.info(`Création de ${pendingClientLivraisons.length} livraison(s) depuis les clients...`);
        
        for (const client of pendingClientLivraisons) {
          // Create livraison from client
          const livraisonData = {
            client_nom: client.nom,
            client_telephone: client.telephone || "",
            adresse: client.adresse,
            ville: client.ville,
            code_postal: client.code_postal || "",
            pays: "Belgique",
            details_commande: "",
            quantite: 1,
            prix: null,
            notes: "",
            date_livraison_prevue: formData.date,
          };
          
          const createRes = await axios.post(`${API}/livraisons`, livraisonData, { headers });
          allLivraisonIds.push(createRes.data.id);
        }
        
        // Geocode all new livraisons
        const newIds = allLivraisonIds.slice(selectedLivraisons.length);
        if (newIds.length > 0) {
          await axios.post(`${API}/livraisons/geocode`, { ids: newIds }, { headers });
        }
      }
      
      const response = await axios.post(
        `${API}/tournees`,
        {
          nom: formData.nom,
          date: formData.date,
          livraisons_ids: allLivraisonIds,
          point_depart_adresse: formData.point_depart_adresse || null,
        },
        { headers }
      );
      toast.success("Tournée créée et optimisée !");
      setDialogOpen(false);
      setFormData({ nom: "", date: new Date().toISOString().slice(0, 10), point_depart_adresse: "" });
      setSelectedLivraisons([]);
      setPendingClientLivraisons([]);
      setCreateDialogTab("livraisons");
      fetchData();
      setSelectedTournee(response.data.id);
      fetchTourneeDetails(response.data.id);
    } catch (error) {
      toast.error("Erreur lors de la création");
      console.error(error);
    }
  };

  const handleOptimize = async () => {
    if (!selectedTournee) return;

    setOptimizing(true);
    try {
      const response = await axios.post(`${API}/tournees/${selectedTournee}/optimize`, {}, { headers });
      toast.success("Route optimisée !");
      fetchTourneeDetails(selectedTournee);
    } catch (error) {
      toast.error("Erreur lors de l'optimisation");
    } finally {
      setOptimizing(false);
    }
  };

  const handleReverse = async () => {
    if (!selectedTournee) return;
    setOptimizing(true);
    try {
      await axios.post(`${API}/tournees/${selectedTournee}/reverse`, {}, { headers });
      toast.success("Ordre inversé — trajet recalculé");
      fetchTourneeDetails(selectedTournee);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'inversion");
    } finally {
      setOptimizing(false);
    }
  };

  const handleDeleteTournee = async (tourneeId) => {
    if (!window.confirm("Supprimer cette tournée ?")) return;

    try {
      await axios.delete(`${API}/tournees/${tourneeId}`, { headers });
      toast.success("Tournée supprimée");
      if (selectedTournee === tourneeId) {
        setSelectedTournee(null);
        setTourneeDetails(null);
      }
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleStatutChange = async (tourneeId, newStatut) => {
    // If starting the tour, show loading summary first
    if (newStatut === "en_cours") {
      setPendingStatutChange({ tourneeId, newStatut });
      // Always re-fetch to avoid stale cache (e.g. after editing a livraison from another page)
      try {
        const response = await axios.get(`${API}/tournees/${tourneeId}/livraisons`, { headers });
        setTourneeDetails(response.data);
      } catch { /* ignore */ }
      setChargementDialogOpen(true);
      return;
    }
    await executeStatutChange(tourneeId, newStatut);
  };

  const executeStatutChange = async (tourneeId, newStatut) => {
    try {
      await axios.put(`${API}/tournees/${tourneeId}`, { statut: newStatut }, { headers });
      toast.success("Statut mis a jour");
      fetchData();
      if (tourneeDetails && tourneeDetails.tournee.id === tourneeId) {
        fetchTourneeDetails(tourneeId);
      }
    } catch (error) {
      toast.error("Erreur lors de la mise a jour");
    }
  };

  const confirmStartTournee = async () => {
    if (pendingStatutChange) {
      await executeStatutChange(pendingStatutChange.tourneeId, pendingStatutChange.newStatut);
    }
    setChargementDialogOpen(false);
    setPendingStatutChange(null);
  };

  // Compute loading summary from tournee details — preserve order of arrêts (1er = chargé en dernier dans le camion)
  const getChargementSummary = () => {
    if (!tourneeDetails) return [];
    const summary = {};
    // Iterate in tour order so later we can show "for which client"
    for (const l of tourneeDetails.livraisons) {
      const plies = Array.isArray(l.produits_lies) ? l.produits_lies : [];
      if (plies.length > 0) {
        for (const pl of plies) {
          const key = `${pl.modele || "?"} — ${pl.couleur || "?"}`;
          if (!summary[key]) summary[key] = { modele: pl.modele || "?", couleur: pl.couleur || "", quantite: 0 };
          // Use ONLY produits_lies quantity. Falling back on l.quantite (total) for mixed orders would double-count.
          summary[key].quantite += Number(pl.quantite) || 0;
        }
      } else if (l.details_commande) {
        // Fallback: no produits_lies, use details_commande as-is
        const key = l.details_commande;
        if (!summary[key]) summary[key] = { modele: l.details_commande, couleur: "", quantite: 0 };
        summary[key].quantite += Number(l.quantite) || 1;
      } else {
        const key = "Non specifie";
        if (!summary[key]) summary[key] = { modele: "Non specifie", couleur: "", quantite: 0 };
        summary[key].quantite += Number(l.quantite) || 1;
      }
    }
    return Object.values(summary);
  };

  const handleReorder = async (newOrder) => {
    if (!selectedTournee) return;

    try {
      await axios.put(
        `${API}/tournees/${selectedTournee}`,
        { ordre_livraisons: newOrder },
        { headers }
      );
      toast.success("Ordre mis à jour");
      fetchTourneeDetails(selectedTournee);
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const moveItem = (fromIndex, toIndex) => {
    if (!tourneeDetails) return;
    const newOrder = [...tourneeDetails.livraisons.map((l) => l.id)];
    const [movedItem] = newOrder.splice(fromIndex, 1);
    newOrder.splice(toIndex, 0, movedItem);
    handleReorder(newOrder);
  };

  // Calculate estimated times and build SMS messages
  // Groups deliveries by client (same name + phone) into a single message
  const buildSmsMessages = (startTime, durationMinutes, dateStr) => {
    if (!tourneeDetails) return [];
    const [h, m] = startTime.split(":").map(Number);
    const livs = tourneeDetails.livraisons;
    const totalMinutes = h * 60 + m;
    const travelBetween = livs.length > 1
      ? Math.round((tourneeDetails.tournee.duree_totale_minutes || livs.length * 15) / livs.length)
      : 15;

    // Jour de semaine FR + date FR (DD/MM/YYYY)
    const effectiveDate = dateStr || smsDate || new Date().toISOString().slice(0, 10);
    const dObj = new Date(`${effectiveDate}T12:00:00`);
    const jourSemaine = dObj.toLocaleDateString("fr-FR", { weekday: "long" });
    const dateFR = dObj.toLocaleDateString("fr-FR");

    const fmt = (mins) => {
      const hh = Math.floor(mins / 60) % 24;
      const mm = mins % 60;
      return `${hh}h${mm.toString().padStart(2, "0")}`;
    };

    // Group deliveries by client (same normalized name + same phone)
    const normalize = (s) => (s || "").toLowerCase().trim().replace(/\s+/g, " ");
    const clientGroups = {};
    
    livs.forEach((l, idx) => {
      const clientKey = `${normalize(l.client_nom)}|${(l.client_telephone || "").replace(/\s/g, "")}`;
      if (!clientGroups[clientKey]) {
        clientGroups[clientKey] = {
          livraisons: [],
          firstIndex: idx,
          client_nom: l.client_nom,
          client_telephone: l.client_telephone,
          adresse: l.adresse,
          code_postal: l.code_postal,
          ville: l.ville,
        };
      }
      clientGroups[clientKey].livraisons.push(l);
    });

    // Build one message per client group
    return Object.values(clientGroups).map((group) => {
      const firstIdx = group.firstIndex;
      const startMin = totalMinutes + firstIdx * (durationMinutes + travelBetween);
      const endMin = startMin + 20;
      const heureDebut = fmt(startMin);
      const heureFin = fmt(endMin);
      const prenom = group.client_nom.split(" ")[0];

      // Aggregate all products and prices
      const allProducts = group.livraisons.map(l => l.details_commande).filter(Boolean);
      const productsSummary = allProducts.length > 1 
        ? allProducts.join(" + ")
        : allProducts[0] || "";
      
      // Sum prices
      const totalPrix = group.livraisons.reduce((sum, l) => {
        return sum + (l.prix != null ? l.prix : 0);
      }, 0);
      const hasAllPrices = group.livraisons.every(l => l.prix != null);
      const prixStr = hasAllPrices && totalPrix > 0 ? `${totalPrix} €` : "à confirmer";

      // Get command numbers (show all if multiple)
      const numCmds = group.livraisons
        .filter(l => l.numero_commande)
        .map(l => `#${l.numero_commande}`);
      const numCmdStr = numCmds.length > 0 ? numCmds.join(", ") : "";

      // Lien de confirmation unique par client (Chantier 2)
      // Utilise le numéro de commande si disponible, sinon fallback UUID court (6 caractères)
      const firstLiv = group.livraisons[0];
      const identifier = firstLiv.numero_commande != null
        ? String(firstLiv.numero_commande)
        : (firstLiv.id || "").slice(0, 6);
      const lienConfirmation = `https://topcocoon.support/confirmation/${identifier}`;

      // Rendu du template : DB (éditable via Paramètres) sinon fallback hardcodé
      const applyVars = (tpl) => (tpl || "")
        .replaceAll("{prenom}", prenom || "")
        .replaceAll("{jour_semaine}", jourSemaine || "")
        .replaceAll("{date}", dateFR || "")
        .replaceAll("{heure_debut}", heureDebut || "")
        .replaceAll("{heure_fin}", heureFin || "")
        .replaceAll("{adresse}", group.adresse || "")
        .replaceAll("{code_postal}", group.code_postal || "")
        .replaceAll("{ville}", group.ville || "")
        .replaceAll("{details_commande}", productsSummary || "")
        .replaceAll("{prix}", prixStr || "")
        .replaceAll("{lien_confirmation}", lienConfirmation)
        .replaceAll("{nom_societe}", "TopCocoon")
        .replace(/\{[a-z_]+\}/g, "");

      const msg = smsTemplateBody
        ? applyVars(smsTemplateBody)
        : (
          `Bonjour ${prenom}, votre livraison TopCocoon est prévue ce ${jourSemaine} ${dateFR} entre ${heureDebut} et ${heureFin}.\n` +
          `📍 ${group.adresse}, ${group.code_postal} ${group.ville}\n` +
          `🪑 Commande : ${productsSummary}\n` +
          `💰 Total : ${prixStr}, livraison comprise.\n` +
          `💶 Paiement à la livraison : en espèces uniquement. Notre livreur ne dispose pas d'un terminal de paiement. Merci de prévoir le montant exact.\n` +
          `📞 Le livreur vous contactera environ 15 minutes avant son arrivée. Merci de rester joignable.\n` +
          `✅ Merci de confirmer votre présence via ce lien : ${lienConfirmation}\n` +
          `L'équipe TopCocoon`
        );

      // Return first livraison as reference, but keep all livraisons for updating heure_estimee
      return { 
        livraison: group.livraisons[0], 
        allLivraisons: group.livraisons,
        msg, 
        heureDebut, 
        heureFin, 
        heureEstimee: heureDebut,
        isGrouped: group.livraisons.length > 1,
        groupSize: group.livraisons.length,
      };
    });
  };

  const handleOpenSmsDialog = () => {
    const msgs = buildSmsMessages(smsStartTime, smsDuration, smsDate);
    setSmsMessages(msgs);
    setSmsDialogOpen(true);
  };

  const handleBonLivraison = async () => {
    if (!selectedTournee) return;
    try {
      const response = await axios.get(`${API}/tournees/${selectedTournee}/bon-livraison`, {
        headers,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `bon_tournee.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Bon de tournée téléchargé !");
    } catch {
      toast.error("Erreur lors de la génération du bon");
    }
  };

  const handleDownloadChargementPdf = async () => {
    const tourneeId = tourneeDetails?.tournee?.id || selectedTournee;
    if (!tourneeId) return;
    try {
      const response = await axios.get(`${API}/tournees/${tourneeId}/chargement-pdf`, {
        headers,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `chargement_${tourneeDetails?.tournee?.nom || "tournee"}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF chargement telecharge !");
    } catch {
      toast.error("Erreur lors de la generation du PDF");
    }
  };

  const handleSendSms = async (item) => {
    // Save heure_estimee to ALL livraisons in the group (for grouped clients)
    try {
      const livraisonsToUpdate = item.allLivraisons || [item.livraison];
      await Promise.all(livraisonsToUpdate.map(l => 
        axios.put(`${API}/livraisons/${l.id}`, {
          heure_estimee: item.heureEstimee
        }, { headers })
      ));
    } catch {}
    // Open SMS app
    const tel = item.livraison.client_telephone;
    if (tel) {
      window.location.href = `sms:${tel}?body=${encodeURIComponent(item.msg)}`;
    } else {
      toast.error("Pas de numéro de téléphone pour ce client");
    }
  };

  // ===== Tournée historique handlers =====
  const handleCreateHistorique = async (e) => {
    e.preventDefault();
    const validClients = historiqueClients.filter(c => c.nom.trim());
    if (validClients.length === 0) {
      toast.error("Ajoutez au moins un client");
      return;
    }
    try {
      const payload = {
        nom: historiqueForm.nom,
        date: historiqueForm.date,
        deduire_stock: historiqueForm.deduire_stock,
        clients: validClients.map(c => ({
          nom: c.nom,
          telephone: c.telephone,
          details_commande: c.details_commande,
          quantite: parseInt(c.quantite) || 1,
          prix: c.prix !== "" ? parseFloat(c.prix) : null,
        })),
        enveloppe_montant: historiqueForm.enveloppe_montant !== "" ? parseFloat(historiqueForm.enveloppe_montant) : null,
        enveloppe_nb_chaises: historiqueForm.enveloppe_nb_chaises !== "" ? parseInt(historiqueForm.enveloppe_nb_chaises) : null,
      };
      await axios.post(`${API}/tournees/historique`, payload, { headers });
      toast.success("Tournée historique enregistrée !");
      setHistoriqueDialogOpen(false);
      setHistoriqueForm({
        nom: "", date: new Date(Date.now() - 86400000).toISOString().slice(0, 10),
        deduire_stock: false, enveloppe_montant: "", enveloppe_nb_chaises: "",
      });
      setHistoriqueClients([{ id: Date.now(), nom: "", telephone: "", details_commande: "", quantite: 1, prix: "" }]);
      fetchData();
    } catch (err) {
      toast.error("Erreur lors de la création");
      console.error(err);
    }
  };

  const openEnveloppeDialog = (tournee) => {
    setEnveloppeDialogTourneeId(tournee.id);
    setEnveloppeForm({
      montant: tournee.enveloppe_montant ?? "",
      nb_chaises: tournee.enveloppe_nb_chaises ?? "",
      remise: tournee.enveloppe_remise || false,
      date_remise: tournee.enveloppe_date_remise ? tournee.enveloppe_date_remise.slice(0, 10) : "",
      virement_cocoon_montant: tournee.virement_cocoon_montant ?? "",
      virement_livreur_montant: tournee.virement_livreur_montant ?? "",
      virement_livreur_nom: tournee.virement_livreur_nom ?? "",
      virement_remise: tournee.virement_remise || false,
      virement_date_remise: tournee.virement_date_remise ? tournee.virement_date_remise.slice(0, 10) : "",
    });
    setEnveloppeDialogOpen(true);
  };

  const handleUpdateEnveloppe = async (e) => {
    e.preventDefault();
    if (!enveloppeDialogTourneeId) return;
    try {
      await axios.put(`${API}/tournees/${enveloppeDialogTourneeId}`, {
        enveloppe_montant: enveloppeForm.montant !== "" ? parseFloat(enveloppeForm.montant) : null,
        enveloppe_nb_chaises: enveloppeForm.nb_chaises !== "" ? parseInt(enveloppeForm.nb_chaises) : null,
        enveloppe_remise: enveloppeForm.remise,
        enveloppe_date_remise: enveloppeForm.date_remise || null,
        virement_cocoon_montant: enveloppeForm.virement_cocoon_montant !== "" ? parseFloat(enveloppeForm.virement_cocoon_montant) : null,
        virement_livreur_montant: enveloppeForm.virement_livreur_montant !== "" ? parseFloat(enveloppeForm.virement_livreur_montant) : null,
        virement_livreur_nom: enveloppeForm.virement_livreur_nom || null,
        virement_remise: enveloppeForm.virement_remise,
        virement_date_remise: enveloppeForm.virement_date_remise || null,
      }, { headers });
      toast.success("Compte-rendu financier mis à jour !");
      setEnveloppeDialogOpen(false);
      fetchData();
      if (tourneeDetails && tourneeDetails.tournee.id === enveloppeDialogTourneeId) {
        fetchTourneeDetails(enveloppeDialogTourneeId);
      }
    } catch (err) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleQuickRemiseEnveloppe = async (tourneeId) => {
    try {
      await axios.put(`${API}/tournees/${tourneeId}`, {
        enveloppe_remise: true,
        enveloppe_date_remise: new Date().toISOString().slice(0, 10),
      }, { headers });
      toast.success("Enveloppe marquée comme remise !");
      fetchData();
    } catch {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  // ===== Frais tournée (essence, adblue, etc.) =====
  const openFraisDialog = (tourneeId) => {
    setFraisDialogTourneeId(tourneeId);
    setFraisForm({ type: "essence", montant: "", note: "" });
    setFraisDialogOpen(true);
  };
  const handleAddFrais = async (e) => {
    e.preventDefault();
    if (!fraisDialogTourneeId || !fraisForm.montant) return;
    setFraisSubmitting(true);
    try {
      await axios.post(`${API}/tournees/${fraisDialogTourneeId}/frais`, {
        type: fraisForm.type,
        montant: parseFloat(fraisForm.montant),
        note: fraisForm.note || "",
      }, { headers });
      toast.success("Frais ajouté et Charge créée automatiquement !");
      setFraisDialogOpen(false);
      if (selectedTournee === fraisDialogTourneeId) fetchTourneeDetails(selectedTournee);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur ajout frais");
    } finally {
      setFraisSubmitting(false);
    }
  };
  const handleDeleteFrais = async (tourneeId, fraisId) => {
    if (!window.confirm("Supprimer ce frais ? La Charge liée sera également supprimée.")) return;
    try {
      await axios.delete(`${API}/tournees/${tourneeId}/frais/${fraisId}`, { headers });
      toast.success("Frais supprimé");
      if (selectedTournee === tourneeId) fetchTourneeDetails(selectedTournee);
      fetchData();
    } catch {
      toast.error("Erreur suppression");
    }
  };

  // ===== Fusion de tournées =====
  const toggleTourneeForMerge = (id) => {
    setSelectedTourneesForMerge(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const openMergeDialog = () => {
    const ids = Array.from(selectedTourneesForMerge);
    if (ids.length < 2) {
      toast.error("Sélectionne au moins 2 tournées pour fusionner");
      return;
    }
    setMergePrimaryId(ids[0]);
    // default date = most recent tournée's date
    const picks = tournees.filter(t => ids.includes(t.id));
    const latestDate = picks.map(t => t.date).sort().reverse()[0] || "";
    setMergeDate(latestDate);
    setMergeDialogOpen(true);
  };
  const handleMerge = async () => {
    const ids = Array.from(selectedTourneesForMerge);
    const secondary_ids = ids.filter(x => x !== mergePrimaryId);
    if (secondary_ids.length === 0) return;
    setMergeSubmitting(true);
    try {
      const res = await axios.post(`${API}/tournees/merge`, {
        primary_id: mergePrimaryId,
        secondary_ids,
        date: mergeDate || undefined,
      }, { headers });
      toast.success(res.data.message || "Tournées fusionnées !");
      setMergeDialogOpen(false);
      setSelectedTourneesForMerge(new Set());
      setSelectedTournee(mergePrimaryId);
      fetchData();
      fetchTourneeDetails(mergePrimaryId);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur fusion");
    } finally {
      setMergeSubmitting(false);
    }
  };

  const addHistoriqueClientRow = () => {
    setHistoriqueClients([...historiqueClients, { id: Date.now(), nom: "", telephone: "", details_commande: "", quantite: 1, prix: "" }]);
  };

  const removeHistoriqueClientRow = (id) => {
    if (historiqueClients.length > 1) {
      setHistoriqueClients(historiqueClients.filter(c => c.id !== id));
    }
  };

  const updateHistoriqueClient = (id, field, value) => {
    setHistoriqueClients(historiqueClients.map(c => c.id === id ? { ...c, [field]: value } : c));
  };

  useEffect(() => {
    fetchData();
    // Charge les templates SMS éditables (Paramètres > Messages)
    (async () => {
      try {
        const [tl, ta] = await Promise.all([
          axios.get(`${API}/message-templates/sms_tournee_livraison`, { headers }),
          axios.get(`${API}/message-templates/sms_tournee_avis`, { headers }),
        ]);
        setSmsTemplateBody(tl.data?.body || "");
        setSmsAvisTemplateBody(ta.data?.body || "");
      } catch { /* ignore */ }
    })();
  }, []);

  useEffect(() => {
    if (selectedTournee) {
      fetchTourneeDetails(selectedTournee);
    }
  }, [selectedTournee]);

  // Prepare map data
  const mapBounds = [];
  const routeCoordinates = [];

  if (tourneeDetails) {
    const depot = tourneeDetails.point_depart;
    mapBounds.push([depot.lat, depot.lng]);

    tourneeDetails.livraisons.forEach((l) => {
      if (l.latitude && l.longitude) {
        mapBounds.push([l.latitude, l.longitude]);
      }
    });

    // Parse route geometry if available
    if (tourneeDetails.tournee.route_geometry?.coordinates) {
      tourneeDetails.tournee.route_geometry.coordinates.forEach((coord) => {
        routeCoordinates.push([coord[1], coord[0]]);
      });
    }
  }

  const availableLivraisons = allLivraisons.filter(
    (l) => l.latitude && l.longitude && !l.tournee_id && l.statut !== "livree" && l.statut !== "annulee"
  );

  return (
    <div className="space-y-6 animate-fade-in" data-testid="tournees-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-800">Tournées</h1>
          <p className="text-slate-500 mt-1">Planification et optimisation des routes de livraison</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <a href="/livreur" target="_blank" rel="noopener noreferrer">
            <Button variant="outline" className="bg-green-50 border-green-200 text-green-700 hover:bg-green-100" data-testid="mode-livreur-btn">
              <Play className="w-4 h-4 mr-2" />
              Mode Livreur
            </Button>
          </a>
          <Button
            variant="outline"
            onClick={async () => {
              try {
                const res = await axios.get(`${API}/export/tournees/excel`, { headers, responseType: "blob" });
                const url = window.URL.createObjectURL(new Blob([res.data]));
                const a = document.createElement("a");
                a.href = url;
                a.download = `tournees_topcocoon_${new Date().toISOString().slice(0,10)}.xlsx`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                toast.success("Export Excel téléchargé !");
              } catch {
                toast.error("Erreur export");
              }
            }}
            className="bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100"
            data-testid="export-tournees-excel-btn"
          >
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Export Excel
          </Button>
          <Button
            variant="outline"
            className="bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100"
            onClick={() => setHistoriqueDialogOpen(true)}
            data-testid="create-tournee-historique-btn"
          >
            <History className="w-4 h-4 mr-2" />
            Tournée passée
          </Button>
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) {
              setCreateDialogTab("livraisons");
              setCreateClientSearch("");
              setPendingClientLivraisons([]);
            }
          }}>
            <DialogTrigger asChild>
              <Button className="bg-indigo-600 hover:bg-indigo-700" data-testid="create-tournee-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nouvelle tournée
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="tournee-dialog">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Route className="w-5 h-5 text-indigo-600" />
                  Créer une tournée
                </DialogTitle>
              </DialogHeader>
            <form onSubmit={handleCreateTournee} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nom de la tournée *</Label>
                  <Input
                    value={formData.nom}
                    onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                    placeholder="Tournée Bruxelles Nord"
                    required
                    data-testid="tournee-nom-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Date</Label>
                  <Input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    data-testid="tournee-date-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Point de départ (optionnel)</Label>
                <Input
                  value={formData.point_depart_adresse}
                  onChange={(e) => setFormData({ ...formData, point_depart_adresse: e.target.value })}
                  placeholder="Prijkelstraat 3, 9840 Nazareth, Belgique (défaut)"
                  data-testid="tournee-depart-input"
                />
                <p className="text-xs text-slate-500">
                  Laissez vide pour utiliser le dépôt par défaut
                </p>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Sélectionner les arrêts ({selectedLivraisons.length + pendingClientLivraisons.length} sélectionnés)</Label>
                </div>
                
                {/* Tabs for livraisons vs clients */}
                <div className="flex gap-2 border-b">
                  <button
                    type="button"
                    className={`px-3 py-1.5 text-sm font-medium border-b-2 transition-colors ${
                      createDialogTab === "livraisons" 
                        ? "border-indigo-600 text-indigo-600" 
                        : "border-transparent text-slate-500 hover:text-slate-700"
                    }`}
                    onClick={() => setCreateDialogTab("livraisons")}
                  >
                    <Package className="w-4 h-4 inline mr-1" />
                    Livraisons ({availableLivraisons.length})
                  </button>
                  <button
                    type="button"
                    className={`px-3 py-1.5 text-sm font-medium border-b-2 transition-colors ${
                      createDialogTab === "clients" 
                        ? "border-indigo-600 text-indigo-600" 
                        : "border-transparent text-slate-500 hover:text-slate-700"
                    }`}
                    onClick={() => setCreateDialogTab("clients")}
                  >
                    <Users className="w-4 h-4 inline mr-1" />
                    Clients CRM ({clients.filter(c => c.adresse && c.ville).length})
                  </button>
                </div>

                {/* Tab content */}
                {createDialogTab === "livraisons" ? (
                  <div className="border rounded-lg max-h-64 overflow-y-auto">
                    {availableLivraisons.length === 0 ? (
                      <div className="p-4 text-center text-slate-500 space-y-2">
                        <Package className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                        <p>Aucune livraison disponible</p>
                        <p className="text-xs">Utilisez l'onglet "Clients CRM" pour ajouter des clients directement</p>
                      </div>
                    ) : (
                      availableLivraisons.map((l) => (
                        <div
                          key={l.id}
                          className="flex items-center gap-3 p-3 border-b last:border-b-0 hover:bg-slate-50"
                        >
                          <Checkbox
                            checked={selectedLivraisons.includes(l.id)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setSelectedLivraisons([...selectedLivraisons, l.id]);
                              } else {
                                setSelectedLivraisons(selectedLivraisons.filter((id) => id !== l.id));
                              }
                            }}
                            data-testid={`select-livraison-${l.id}`}
                          />
                          <div className="flex-1">
                            <p className="font-medium text-sm">{l.client_nom}</p>
                            <p className="text-xs text-slate-500">
                              {l.adresse}, {l.code_postal} {l.ville}
                            </p>
                          </div>
                          <Badge variant="outline" className="text-xs">
                            {l.quantite}x
                          </Badge>
                        </div>
                      ))
                    )}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {/* Search clients */}
                    <div className="relative">
                      <Input
                        type="text"
                        placeholder="Rechercher un client..."
                        value={createClientSearch}
                        onChange={(e) => setCreateClientSearch(e.target.value)}
                        className="pl-9"
                      />
                      <Users className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    </div>
                    
                    {/* Selected clients chips */}
                    {pendingClientLivraisons.length > 0 && (
                      <div className="flex flex-wrap gap-2 p-2 bg-indigo-50 rounded-lg">
                        {pendingClientLivraisons.map((c) => (
                          <span key={c.id} className="inline-flex items-center gap-1 px-2 py-1 bg-white border border-indigo-200 rounded-full text-sm">
                            {c.nom}
                            <button
                              type="button"
                              onClick={() => setPendingClientLivraisons(pendingClientLivraisons.filter(pc => pc.id !== c.id))}
                              className="text-slate-400 hover:text-red-500"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                    
                    {/* Clients list */}
                    <div className="border rounded-lg max-h-52 overflow-y-auto">
                      {clients.filter(c => 
                        c.adresse && c.ville &&
                        !pendingClientLivraisons.find(pc => pc.id === c.id) &&
                        (createClientSearch === "" || 
                         c.nom.toLowerCase().includes(createClientSearch.toLowerCase()) ||
                         c.telephone?.includes(createClientSearch))
                      ).length === 0 ? (
                        <div className="p-4 text-center text-slate-500">
                          <Users className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                          <p className="text-sm">Aucun client disponible</p>
                          <p className="text-xs mt-1">Les clients doivent avoir une adresse complète</p>
                        </div>
                      ) : (
                        clients.filter(c => 
                          c.adresse && c.ville &&
                          !pendingClientLivraisons.find(pc => pc.id === c.id) &&
                          (createClientSearch === "" || 
                           c.nom.toLowerCase().includes(createClientSearch.toLowerCase()) ||
                           c.telephone?.includes(createClientSearch))
                        ).map((c) => (
                          <div
                            key={c.id}
                            className="flex items-center gap-3 p-3 border-b last:border-b-0 hover:bg-slate-50 cursor-pointer"
                            onClick={() => setPendingClientLivraisons([...pendingClientLivraisons, c])}
                          >
                            <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                              <span className="text-indigo-600 font-semibold text-xs">
                                {c.nom.charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm truncate">{c.nom}</p>
                              <p className="text-xs text-slate-500 truncate">
                                {c.adresse}, {c.code_postal} {c.ville}
                              </p>
                            </div>
                            <Plus className="w-4 h-4 text-indigo-600 flex-shrink-0" />
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>

              <Button
                type="submit"
                className="w-full bg-indigo-600 hover:bg-indigo-700"
                disabled={selectedLivraisons.length === 0 && pendingClientLivraisons.length === 0}
                data-testid="submit-tournee-btn"
              >
                <Route className="w-4 h-4 mr-2" />
                Créer et optimiser la tournée
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Tournees List */}
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Tournées ({tournees.length})</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Filtre plage de dates + tout sélectionner + export bulk (Phase 3) */}
              <div className="p-2 bg-slate-50 border border-slate-200 rounded-lg space-y-2" data-testid="tournee-bulk-toolbar">
                <div className="flex flex-wrap items-end gap-2">
                  <div className="flex-1 min-w-[120px]">
                    <label className="text-[10px] uppercase text-slate-500 font-medium">Date début</label>
                    <input type="date" value={tourneeDateFilter.debut} onChange={(e) => setTourneeDateFilter({ ...tourneeDateFilter, debut: e.target.value })} className="w-full h-8 px-2 text-xs border rounded-md" data-testid="tournee-filter-date-debut" />
                  </div>
                  <div className="flex-1 min-w-[120px]">
                    <label className="text-[10px] uppercase text-slate-500 font-medium">Date fin</label>
                    <input type="date" value={tourneeDateFilter.fin} onChange={(e) => setTourneeDateFilter({ ...tourneeDateFilter, fin: e.target.value })} className="w-full h-8 px-2 text-xs border rounded-md" data-testid="tournee-filter-date-fin" />
                  </div>
                  {(tourneeDateFilter.debut || tourneeDateFilter.fin) && (
                    <Button size="sm" variant="ghost" className="h-8 px-2 text-xs" onClick={() => setTourneeDateFilter({ debut: "", fin: "" })} data-testid="tournee-filter-clear">Effacer</Button>
                  )}
                </div>
                {(() => {
                  const visible = tournees.filter((t) => {
                    if (tourneeDateFilter.debut && (!t.date_prevue || t.date_prevue < tourneeDateFilter.debut)) return false;
                    if (tourneeDateFilter.fin && (!t.date_prevue || t.date_prevue > tourneeDateFilter.fin)) return false;
                    return true;
                  }).filter((t) => t.statut !== "en_cours");
                  const allSelected = visible.length > 0 && visible.every((t) => selectedTourneesForMerge.has(t.id));
                  return (
                    <div className="flex flex-wrap items-center gap-2">
                      <Button
                        size="sm" variant="outline" className="h-8 text-xs"
                        onClick={() => {
                          if (allSelected) setSelectedTourneesForMerge(new Set());
                          else setSelectedTourneesForMerge(new Set(visible.map((t) => t.id)));
                        }}
                        disabled={visible.length === 0}
                        data-testid="tournee-select-all"
                      >
                        {allSelected ? "Tout désélectionner" : `Tout sélectionner (${visible.length})`}
                      </Button>
                      {selectedTourneesForMerge.size > 0 && (
                        <>
                          <span className="text-xs text-slate-600">
                            <b>{selectedTourneesForMerge.size}</b> sélectionnée(s)
                          </span>
                          <Button
                            size="sm" className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700 ml-auto"
                            onClick={async () => {
                              if (bulkExporting) return;
                              setBulkExporting(true);
                              try {
                                const res = await axios.post(
                                  `${API}/tournees/bulk-bon-livraison`,
                                  { tournee_ids: Array.from(selectedTourneesForMerge) },
                                  { headers, responseType: "blob" }
                                );
                                const url = window.URL.createObjectURL(new Blob([res.data]));
                                const a = document.createElement("a");
                                a.href = url;
                                const stamp = new Date().toISOString().slice(0, 10);
                                a.setAttribute("download", `bons_tournees_${stamp}.pdf`);
                                document.body.appendChild(a);
                                a.click();
                                a.remove();
                                toast.success(`${selectedTourneesForMerge.size} bon(s) exporté(s) en 1 PDF ✓`);
                              } catch (err) {
                                toast.error(err?.response?.data?.detail || "Erreur export bulk");
                              } finally {
                                setBulkExporting(false);
                              }
                            }}
                            disabled={bulkExporting}
                            data-testid="tournee-bulk-export-btn"
                          >
                            {bulkExporting ? "Export…" : "Imprimer / Exporter la sélection"}
                          </Button>
                        </>
                      )}
                    </div>
                  );
                })()}
              </div>

              {/* Merge action bar */}
              {selectedTourneesForMerge.size >= 2 && (
                <div className="flex items-center justify-between gap-2 p-2 bg-indigo-50 border border-indigo-200 rounded-lg" data-testid="merge-bar">
                  <span className="text-xs font-medium text-indigo-700">
                    <b>{selectedTourneesForMerge.size}</b> tournée(s) sélectionnée(s)
                  </span>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setSelectedTourneesForMerge(new Set())} data-testid="merge-clear-btn">Annuler</Button>
                    <Button size="sm" className="h-7 px-2 text-xs bg-indigo-600 hover:bg-indigo-700" onClick={openMergeDialog} data-testid="merge-open-btn">
                      <Shuffle className="w-3 h-3 mr-1" />Fusionner
                    </Button>
                  </div>
                </div>
              )}
              {loading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                </div>
              ) : tournees.length === 0 ? (
                <p className="text-center py-8 text-slate-500">Aucune tournée</p>
              ) : (
                tournees
                  .filter((t) => {
                    if (tourneeDateFilter.debut && (!t.date_prevue || t.date_prevue < tourneeDateFilter.debut)) return false;
                    if (tourneeDateFilter.fin && (!t.date_prevue || t.date_prevue > tourneeDateFilter.fin)) return false;
                    return true;
                  })
                  .map((tournee) => (
                  <div
                    key={tournee.id}
                    className={`p-4 border rounded-lg cursor-pointer transition-all ${
                      selectedTournee === tournee.id
                        ? "border-indigo-500 bg-indigo-50"
                        : "hover:border-slate-300"
                    }`}
                    onClick={() => setSelectedTournee(tournee.id)}
                    data-testid={`tournee-card-${tournee.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-2">
                        {/* Checkbox fusion — visible sauf tournées en cours de livraison */}
                        {tournee.statut !== "en_cours" && (
                          <input
                            type="checkbox"
                            checked={selectedTourneesForMerge.has(tournee.id)}
                            onChange={(e) => { e.stopPropagation(); toggleTourneeForMerge(tournee.id); }}
                            onClick={(e) => e.stopPropagation()}
                            className="w-4 h-4 mt-1 cursor-pointer"
                            data-testid={`merge-checkbox-${tournee.id}`}
                            title="Cocher pour fusion multi-tournées"
                          />
                        )}
                        <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium">{tournee.nom}</p>
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              const newName = window.prompt("Nouveau nom de la tournée :", tournee.nom);
                              if (!newName || newName.trim() === "" || newName === tournee.nom) return;
                              try {
                                await axios.put(`${API}/tournees/${tournee.id}`, { nom: newName.trim() }, { headers });
                                toast.success("Tournée renommée ✓");
                                fetchData();
                                if (selectedTournee === tournee.id) fetchTourneeDetails(tournee.id);
                              } catch { toast.error("Erreur renommage"); }
                            }}
                            className="text-slate-400 hover:text-indigo-600"
                            title="Renommer la tournée"
                            data-testid={`rename-tournee-btn-${tournee.id}`}
                          >
                            <Edit className="w-3.5 h-3.5" />
                          </button>
                          {tournee.is_historique && (
                            <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-medium flex items-center gap-1">
                              <History className="w-3 h-3" />
                              Historique
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-slate-500">{tournee.date}</p>
                        </div>
                      </div>
                      <Badge
                        className={
                          STATUT_OPTIONS.find((o) => o.value === tournee.statut)?.color ||
                          "bg-slate-100"
                        }
                      >
                        {STATUT_OPTIONS.find((o) => o.value === tournee.statut)?.label || tournee.statut}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-4 mt-3 text-sm text-slate-600">
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {((selectedTournee === tournee.id && tourneeDetails?.livraisons)
                          ? tourneeDetails.livraisons.length
                          : (tournee.livraisons_ids?.length || 0))} arrêts
                      </span>
                      <span className="flex items-center gap-1">
                        <Navigation className="w-4 h-4" />
                        {tournee.distance_totale_km.toFixed(1)} km
                      </span>
                    </div>
                    <div className="flex items-center gap-4 mt-1 text-sm text-slate-600">
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {Math.round(tournee.duree_totale_minutes)} min
                      </span>
                      <span className="flex items-center gap-1">
                        <Fuel className="w-4 h-4" />
                        {tournee.cout_carburant.toFixed(2)} €
                      </span>
                    </div>
                    {/* Enveloppe info */}
                    {(tournee.enveloppe_montant != null || tournee.enveloppe_nb_chaises != null) && (
                      <div className={`mt-2 p-2 rounded-lg border text-xs flex items-center justify-between gap-2 ${
                        tournee.enveloppe_remise 
                          ? "bg-green-50 border-green-200 text-green-700" 
                          : "bg-orange-50 border-orange-200 text-orange-700"
                      }`}>
                        <div className="flex items-center gap-2">
                          <Wallet className="w-3.5 h-3.5 flex-shrink-0" />
                          <span>
                            {tournee.enveloppe_montant != null && <span className="font-semibold">{tournee.enveloppe_montant.toFixed(2)} €</span>}
                            {tournee.enveloppe_nb_chaises != null && <span className="ml-1">· {tournee.enveloppe_nb_chaises} chaise{tournee.enveloppe_nb_chaises > 1 ? "s" : ""}</span>}
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          {tournee.enveloppe_remise ? (
                            <span className="flex items-center gap-1 font-medium"><BadgeCheck className="w-3.5 h-3.5" /> Remise</span>
                          ) : (
                            <button
                              className="underline font-medium hover:opacity-75"
                              onClick={(e) => { e.stopPropagation(); handleQuickRemiseEnveloppe(tournee.id); }}
                              data-testid={`remise-enveloppe-btn-${tournee.id}`}
                            >
                              Marquer remise
                            </button>
                          )}
                          <button
                            className="ml-1 text-slate-500 hover:text-slate-700"
                            onClick={(e) => { e.stopPropagation(); openEnveloppeDialog(tournee); }}
                            title="Modifier l'enveloppe"
                          >
                            <Edit className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    )}
                    {tournee.enveloppe_montant == null && tournee.enveloppe_nb_chaises == null && (
                      <button
                        className="mt-2 w-full text-xs text-slate-400 hover:text-indigo-600 flex items-center justify-center gap-1 py-1 border border-dashed rounded-lg hover:border-indigo-300 transition-colors"
                        onClick={(e) => { e.stopPropagation(); openEnveloppeDialog(tournee); }}
                        data-testid={`add-enveloppe-btn-${tournee.id}`}
                      >
                        <Wallet className="w-3 h-3" />
                        Saisir l'enveloppe
                      </button>
                    )}
                    {/* Bouton Chargement visible */}
                    <Button
                      size="sm"
                      variant="outline"
                      className="mt-3 w-full h-9 text-sm font-medium text-indigo-700 border-indigo-200 bg-indigo-50 hover:bg-indigo-100"
                      onClick={async (e) => {
                        e.stopPropagation();
                        // Toujours rafraîchir pour éviter cache obsolète
                        await fetchTourneeDetails(tournee.id);
                        setPendingStatutChange(null);
                        setChargementDialogOpen(true);
                      }}
                      data-testid={`chargement-btn-${tournee.id}`}
                    >
                      <Package className="w-4 h-4 mr-2" />
                      Preparation du camion
                    </Button>
                    {tournee.statut === "terminee" && (
                      <Button
                        size="sm"
                        className="w-full mt-2 bg-purple-600 hover:bg-purple-700 text-white"
                        onClick={(e) => {
                          e.stopPropagation();
                          window.location.href = `/communications?tournee=${tournee.id}`;
                        }}
                        data-testid={`sms-fin-tournee-btn-${tournee.id}`}
                      >
                        <MessageSquare className="w-4 h-4 mr-2" />
                        Envoyer SMS de fin
                      </Button>
                    )}
                    <div className="flex gap-2 mt-2">
                      <Select
                        value={tournee.statut}
                        onValueChange={(value) => handleStatutChange(tournee.id, value)}
                      >
                        <SelectTrigger className="h-8 text-xs flex-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {STATUT_OPTIONS.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {tournee.statut === "en_cours" && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8 text-xs text-amber-600 border-amber-200 hover:bg-amber-50"
                          onClick={(e) => {
                            e.stopPropagation();
                            executeStatutChange(tournee.id, "planifiee");
                          }}
                          data-testid={`revert-planifiee-btn-${tournee.id}`}
                        >
                          <RefreshCw className="w-3 h-3 mr-1" />
                          Planifiee
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-600 h-8"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteTournee(tournee.id);
                        }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </div>

        {/* Map and Details */}
        <div className="lg:col-span-2 space-y-4">
          {/* Map */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Navigation className="w-5 h-5 text-indigo-600" />
                  Carte de la tournée
                </CardTitle>
                {selectedTournee && (
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleReverse}
                      disabled={optimizing}
                      data-testid="reverse-btn"
                      title="Inverser l'ordre des arrêts (recalcule distance et durée)"
                    >
                      <Shuffle className="w-4 h-4 mr-2" />
                      Inverser
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleOptimize}
                      disabled={optimizing}
                      data-testid="optimize-btn"
                    >
                      <RefreshCw className={`w-4 h-4 mr-2 ${optimizing ? "animate-spin" : ""}`} />
                      Ré-optimiser
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="h-[400px] rounded-lg overflow-hidden border" data-testid="map-container">
                {tourneeDetails ? (
                  <MapContainer
                    center={[tourneeDetails.point_depart.lat, tourneeDetails.point_depart.lng]}
                    zoom={10}
                    style={{ height: "100%", width: "100%" }}
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <FitBounds bounds={mapBounds} />

                    {/* Route line */}
                    {routeCoordinates.length > 0 && (
                      <Polyline
                        positions={routeCoordinates}
                        color="#4f46e5"
                        weight={4}
                        opacity={0.7}
                      />
                    )}

                    {/* Depot marker */}
                    <Marker
                      position={[tourneeDetails.point_depart.lat, tourneeDetails.point_depart.lng]}
                      icon={depotIcon}
                    >
                      <Popup>
                        <div className="text-center">
                          <Warehouse className="w-5 h-5 mx-auto mb-1 text-indigo-600" />
                          <p className="font-medium">Dépôt</p>
                          <p className="text-xs text-slate-500">{tourneeDetails.point_depart.adresse}</p>
                        </div>
                      </Popup>
                    </Marker>

                    {/* Delivery markers */}
                    {tourneeDetails.livraisons.map((l, idx) =>
                      l.latitude && l.longitude ? (
                        <Marker
                          key={l.id}
                          position={[l.latitude, l.longitude]}
                          icon={l.statut === "livree" ? deliveryIcon : deliveryIconPending}
                        >
                          <Popup>
                            <div>
                              <p className="font-medium">
                                #{idx + 1} - {l.client_nom}
                              </p>
                              <p className="text-xs text-slate-500">{l.adresse}</p>
                              <p className="text-xs text-slate-500">
                                {l.code_postal} {l.ville}
                              </p>
                              {l.details_commande && (
                                <p className="text-xs mt-1">{l.details_commande}</p>
                              )}
                            </div>
                          </Popup>
                        </Marker>
                      ) : null
                    )}
                  </MapContainer>
                ) : (
                  <div className="h-full flex items-center justify-center bg-slate-50">
                    <div className="text-center text-slate-500">
                      <Navigation className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>Sélectionnez une tournée pour voir la carte</p>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Route Details */}
          {tourneeDetails && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg">
                      Ordre des arrêts ({tourneeDetails.livraisons.length})
                    </CardTitle>
                    <p className="text-sm text-slate-500">
                      Cliquez sur un arrêt pour le modifier
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setAddLivraisonDialog(true)}
                    data-testid="add-to-tournee-btn"
                  >
                    <UserPlus className="w-4 h-4 mr-2" />
                    Ajouter
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {/* Sélecteur mode frais de livraison */}
                {tourneeDetails.tournee.statut !== "terminee" && (
                  <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg" data-testid="tournee-mode-frais-block">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-emerald-900">💰 Frais de livraison :</span>
                      {[
                        { v: "zones", label: "Personnalisés (zones)", color: "bg-blue-500" },
                        { v: "forfait", label: "Forfait unique", color: "bg-amber-500" },
                        { v: "offerte", label: "🎁 Offerte (0€)", color: "bg-rose-500" },
                      ].map(opt => {
                        const active = (tourneeDetails.tournee.mode_frais || "zones") === opt.v;
                        return (
                          <button
                            key={opt.v}
                            type="button"
                            onClick={async () => {
                              if (active) return;
                              let montant = null;
                              if (opt.v === "forfait") {
                                const v = window.prompt("Montant du forfait (€) ?", tourneeDetails.tournee.frais_forfait || "25");
                                if (v === null) return;
                                montant = parseFloat(v);
                                if (isNaN(montant)) { toast.error("Montant invalide"); return; }
                              }
                              try {
                                await axios.put(`${API}/tournees/${tourneeDetails.tournee.id}/mode-frais`, { mode_frais: opt.v, montant_forfait: montant }, { headers });
                                toast.success(`Frais recalculés (${opt.label})`);
                                fetchTourneeDetails(tourneeDetails.tournee.id);
                              } catch (err) { toast.error(err?.response?.data?.detail || "Erreur"); }
                            }}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${active ? `${opt.color} text-white shadow` : "bg-white text-slate-700 border border-slate-300 hover:bg-slate-50"}`}
                            data-testid={`mode-frais-${opt.v}`}
                          >
                            {opt.label}
                            {active && opt.v === "forfait" && ` : ${tourneeDetails.tournee.frais_forfait || 25}€`}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Summary */}
                {(() => {
                  const livs = tourneeDetails.livraisons || [];
                  const totalChaises = livs.reduce((s, l) => {
                    const pl = l.produits_lies || [];
                    if (pl.length > 0) return s + pl.reduce((ss, p) => ss + (parseInt(p.quantite) || 0), 0);
                    return s + (parseInt(l.quantite) || 0);
                  }, 0);
                  const totalMontant = livs.reduce((s, l) => s + (parseFloat(l.prix) || 0), 0);
                  const totalFrais = livs.reduce((s, l) => s + (parseFloat(l.frais_livraison) || 0), 0);
                  const emails = livs.map(l => l.client_email).filter(Boolean);
                  const CAP = 100;
                  const depasse = totalChaises > CAP;
                  return (
                    <>
                      {depasse && tourneeDetails.tournee.statut !== "terminee" && (
                        <div className="mb-3 p-3 bg-orange-50 border-2 border-orange-300 rounded-lg flex items-center justify-between flex-wrap gap-3" data-testid="capacity-warning">
                          <div className="flex items-center gap-2">
                            <span className="text-2xl">⚠️</span>
                            <div>
                              <p className="font-bold text-orange-800">{totalChaises} chaises pour un camion de {CAP} max</p>
                              <p className="text-xs text-orange-700">Splitte cette tournée intelligemment sur plusieurs jours.</p>
                            </div>
                          </div>
                          <Button onClick={() => setSplitDialogOpen(true)} className="bg-orange-600 hover:bg-orange-700" data-testid="open-split-dialog-btn">
                            🪓 Splitter en tournées
                          </Button>
                        </div>
                      )}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4 kpi-grid-4">
                        <div className={`p-3 rounded-lg text-center ${depasse ? "bg-orange-100 border-2 border-orange-400" : "bg-emerald-50 border border-emerald-200"}`} data-testid="tournee-total-chaises">
                          <p className={`text-xs font-medium ${depasse ? "text-orange-700" : "text-emerald-700"}`}>🪑 Chaises</p>
                          <p className={`text-2xl font-bold ${depasse ? "text-orange-800" : "text-emerald-800"}`}>{totalChaises}<span className="text-xs ml-1">/{CAP}</span></p>
                        </div>
                        <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-lg text-center" data-testid="tournee-total-montant">
                          <p className="text-xs text-indigo-700 font-medium">💰 Montant total</p>
                          <p className="text-2xl font-bold text-indigo-800">{totalMontant.toFixed(2)}<span className="text-sm ml-1">€</span></p>
                        </div>
                        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-center" data-testid="tournee-total-frais">
                          <p className="text-xs text-blue-700 font-medium">🚚 Frais livraison</p>
                          <p className="text-xl font-bold text-blue-800">{totalFrais.toFixed(2)}<span className="text-sm ml-1">€</span></p>
                        </div>
                        <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg text-center" data-testid="tournee-nb-emails">
                          <p className="text-xs text-slate-600 font-medium">📧 Emails</p>
                          <p className="text-2xl font-bold text-slate-800">{emails.length}<span className="text-xs text-slate-500 ml-1">/{livs.length}</span></p>
                        </div>
                      </div>
                    </>
                  );
                })()}

                {/* Liste des emails (pliable) */}
                {(() => {
                  const emails = (tourneeDetails.livraisons || []).map(l => ({ nom: l.client_nom, email: l.client_email })).filter(e => e.email);
                  if (emails.length === 0) return null;
                  return (
                    <details className="mb-4 bg-slate-50 rounded-lg border border-slate-200 p-2" data-testid="tournee-emails-details">
                      <summary className="text-xs font-medium text-slate-700 cursor-pointer hover:text-slate-900">📧 Voir les {emails.length} emails clients</summary>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {emails.map((e, i) => (
                          <a key={i} href={`mailto:${e.email}`} className="text-xs px-2 py-1 bg-white border border-slate-300 rounded hover:bg-indigo-50" title={e.nom}>
                            {e.email}
                          </a>
                        ))}
                        <button
                          type="button"
                          onClick={() => { navigator.clipboard.writeText(emails.map(e => e.email).join(", ")); toast.success("Emails copiés"); }}
                          className="text-xs px-2 py-1 bg-indigo-600 text-white rounded hover:bg-indigo-700"
                          data-testid="copy-emails-btn"
                        >
                          Copier tous
                        </button>
                      </div>
                    </details>
                  );
                })()}

                <div className="grid grid-cols-3 gap-4 mb-4 p-4 bg-indigo-50 rounded-lg">
                  <div className="text-center">
                    <p className="text-sm text-indigo-600">Distance totale</p>
                    <p className="text-xl font-bold text-indigo-700">
                      {tourneeDetails.tournee.distance_totale_km.toFixed(1)} km
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-indigo-600">Durée estimée</p>
                    <p className="text-xl font-bold text-indigo-700">
                      {Math.round(tourneeDetails.tournee.duree_totale_minutes)} min
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-indigo-600">Coût carburant</p>
                    <p className="text-xl font-bold text-indigo-700">
                      {tourneeDetails.tournee.cout_carburant.toFixed(2)} €
                    </p>
                  </div>
                </div>

                {/* Enveloppe section */}
                {tourneeDetails.tournee && (
                  <div className={`mb-4 p-4 rounded-lg border ${
                    tourneeDetails.tournee.enveloppe_remise 
                      ? "bg-green-50 border-green-200" 
                      : "bg-slate-50 border-slate-200"
                  }`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Wallet className="w-4 h-4 text-slate-600" />
                        <span className="font-semibold text-slate-700 text-sm">Enveloppe</span>
                        {tourneeDetails.tournee.enveloppe_remise && (
                          <span className="text-xs bg-green-200 text-green-800 px-2 py-0.5 rounded-full flex items-center gap-1">
                            <BadgeCheck className="w-3 h-3" /> Remise{tourneeDetails.tournee.enveloppe_date_remise ? ` le ${tourneeDetails.tournee.enveloppe_date_remise.slice(0, 10)}` : ""}
                          </span>
                        )}
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs"
                        onClick={() => openEnveloppeDialog(tourneeDetails.tournee)}
                        data-testid="edit-enveloppe-detail-btn"
                      >
                        <Edit className="w-3 h-3 mr-1" />
                        Modifier
                      </Button>
                    </div>
                    {(tourneeDetails.tournee.enveloppe_montant != null || tourneeDetails.tournee.enveloppe_nb_chaises != null) ? (
                      <div className="flex gap-6 mt-3">
                        {tourneeDetails.tournee.enveloppe_montant != null && (
                          <div>
                            <p className="text-xs text-slate-500">Montant encaissé</p>
                            <p className="text-xl font-bold text-slate-800">{tourneeDetails.tournee.enveloppe_montant.toFixed(2)} €</p>
                          </div>
                        )}
                        {tourneeDetails.tournee.enveloppe_nb_chaises != null && (
                          <div>
                            <p className="text-xs text-slate-500">Nombre de chaises</p>
                            <p className="text-xl font-bold text-slate-800">{tourneeDetails.tournee.enveloppe_nb_chaises}</p>
                          </div>
                        )}
                        {!tourneeDetails.tournee.enveloppe_remise && (
                          <div className="ml-auto self-center">
                            <Button
                              size="sm"
                              className="bg-green-600 hover:bg-green-700 h-8 text-xs"
                              onClick={() => handleQuickRemiseEnveloppe(tourneeDetails.tournee.id)}
                              data-testid="quick-remise-detail-btn"
                            >
                              <BadgeCheck className="w-3.5 h-3.5 mr-1" />
                              Marquer remise
                            </Button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-400 mt-2">Aucune enveloppe saisie — cliquez sur "Modifier" pour ajouter les informations.</p>
                    )}
                  </div>
                )}

                {/* Compte-rendu de fin de tournée — Cash / Virement / Frais */}
                {tourneeDetails.livraisons && tourneeDetails.livraisons.length > 0 && (() => {
                  const livrees = tourneeDetails.livraisons.filter(l => l.statut === "livree");
                  const cashLivs = livrees.filter(l => l.mode_paiement === "liquide");
                  const virLivs = livrees.filter(l => l.mode_paiement === "virement");
                  const cashTotal = cashLivs.reduce((s, l) => s + (l.montant_encaisse || 0), 0);
                  const virTotal = virLivs.reduce((s, l) => s + (l.montant_encaisse || 0), 0);
                  const fraisTotal = (tourneeDetails.tournee.frais_tournee || []).reduce((s, f) => s + (f.montant || 0), 0);
                  const total = cashTotal + virTotal;
                  if (total === 0 && fraisTotal === 0) return null;
                  return (
                    <div className="mb-4 border rounded-lg overflow-hidden" data-testid="cr-tournee-block">
                      <div className="bg-slate-100 px-3 py-2 border-b">
                        <p className="text-sm font-semibold text-slate-700">Compte-rendu de tournée</p>
                      </div>
                      <div className="p-3 space-y-2 text-sm">
                        <div className="flex items-center justify-between">
                          <span className="text-slate-600">💵 Cash à remettre</span>
                          <span className="font-bold text-emerald-700">{cashTotal.toFixed(2)} € <span className="text-xs text-slate-400 font-normal">({cashLivs.length} livr.)</span></span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-slate-600">🏦 Virements reçus</span>
                          <span className="font-bold text-blue-700">{virTotal.toFixed(2)} € <span className="text-xs text-slate-400 font-normal">({virLivs.length} livr.)</span></span>
                        </div>
                        <div className="flex items-center justify-between border-t pt-2">
                          <span className="text-slate-700 font-semibold">Total encaissé</span>
                          <span className="font-bold text-slate-900">{total.toFixed(2)} €</span>
                        </div>
                        {fraisTotal > 0 && (
                          <>
                            <div className="flex items-center justify-between text-amber-700">
                              <span>⛽ Frais réels (essence, péage…)</span>
                              <span className="font-semibold">- {fraisTotal.toFixed(2)} €</span>
                            </div>
                            <div className="flex items-center justify-between border-t pt-2 bg-indigo-50 -mx-3 px-3 py-2">
                              <span className="text-indigo-700 font-bold">Net</span>
                              <span className="font-bold text-indigo-700 text-lg">{(total - fraisTotal).toFixed(2)} €</span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })()}

                {/* Bouton Preparation du camion */}
                <Button
                  className="w-full mb-4 h-11 text-sm font-semibold bg-indigo-600 hover:bg-indigo-700 text-white"
                  onClick={async () => {
                    // Toujours rafraîchir avant ouverture pour éviter d'afficher des données obsolètes
                    if (tourneeDetails?.tournee?.id) {
                      await fetchTourneeDetails(tourneeDetails.tournee.id);
                    }
                    setPendingStatutChange(null);
                    setChargementDialogOpen(true);
                  }}
                  data-testid="chargement-detail-btn"
                >
                  <Package className="w-5 h-5 mr-2" />
                  Preparation du camion
                </Button>

                {/* Depot */}
                <div className="flex items-center gap-3 p-3 bg-indigo-100 rounded-lg mb-2">
                  <div className="w-8 h-8 rounded-full bg-indigo-600 flex items-center justify-center">
                    <Warehouse className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-indigo-800">Départ - Dépôt</p>
                    <p className="text-sm text-indigo-600">{tourneeDetails.point_depart.adresse}</p>
                  </div>
                </div>

                {/* Stops */}
                <div className="space-y-2">
                  {tourneeDetails.livraisons.map((l, idx) => (
                    <div
                      key={l.id}
                      className="flex flex-wrap items-center gap-2 p-3 border rounded-lg hover:bg-slate-50 group"
                      data-testid={`stop-${l.id}`}
                    >
                      <GripVertical 
                        className="w-4 h-4 text-slate-400 cursor-move" 
                        draggable
                        onDragStart={() => setDraggedItem(idx)}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={() => {
                          if (draggedItem !== null && draggedItem !== idx) {
                            moveItem(draggedItem, idx);
                          }
                          setDraggedItem(null);
                        }}
                      />
                      <div
                        className={`w-10 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                          l.statut === "livree" ? "bg-green-500" : "bg-amber-500"
                        }`}
                        title="Tape un numéro pour déplacer cet arrêt à cette position"
                      >
                        <input
                          type="number"
                          min="1"
                          max={tourneeDetails.livraisons.length}
                          defaultValue={idx + 1}
                          key={`pos-${l.id}-${idx}`}
                          className="w-full h-full bg-transparent text-white font-bold text-sm text-center outline-none focus:ring-2 focus:ring-white rounded-full"
                          data-testid={`position-input-${l.id}`}
                          onClick={(e) => e.target.select()}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              e.target.blur();
                            }
                          }}
                          onBlur={(e) => {
                            const val = parseInt(e.target.value, 10);
                            const max = tourneeDetails.livraisons.length;
                            if (!val || val < 1 || val > max || val === idx + 1) {
                              e.target.value = idx + 1;
                              return;
                            }
                            moveItem(idx, val - 1);
                          }}
                        />
                      </div>
                      <div className="flex-1 min-w-[60%] sm:min-w-0">
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                          <p className="font-medium truncate max-w-full">{l.client_nom}</p>
                          {l.numero_commande && (
                            <span className="text-xs font-mono bg-indigo-50 text-indigo-600 px-1.5 py-0.5 rounded whitespace-nowrap">#{l.numero_commande}</span>
                          )}
                          {/* Confirmation badge */}
                          {l.confirmation_presence === "confirme" && (
                            <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap">✓ Confirmé</span>
                          )}
                          {l.confirmation_presence === "absent" && (
                            <span className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap">✗ Absent</span>
                          )}
                          {l.heure_estimee && (
                            <span className="text-xs bg-indigo-50 text-indigo-600 px-1.5 py-0.5 rounded-full whitespace-nowrap">{l.heure_estimee}</span>
                          )}
                          {l.client_telephone && (
                            <a href={`tel:${l.client_telephone}`} className="text-indigo-600 hover:text-indigo-800">
                              <Phone className="w-3 h-3" />
                            </a>
                          )}
                          {l.client_email && (
                            <a href={`mailto:${l.client_email}`} className="text-indigo-600 hover:text-indigo-800" title={l.client_email} data-testid={`arret-email-${l.id}`}>
                              📧
                            </a>
                          )}
                        </div>
                        <p className="text-sm text-slate-500 truncate">
                          {l.adresse}, {l.code_postal} {l.ville}
                        </p>
                        {l.client_email && (
                          <p className="text-xs text-slate-500 truncate">{l.client_email}</p>
                        )}
                        {l.details_commande && (
                          <p className="text-xs text-slate-400 truncate">{l.details_commande}</p>
                        )}
                        {(parseFloat(l.prix) || 0) > 0 && (
                          <p className="text-xs text-emerald-700 font-semibold whitespace-nowrap">💰 {parseFloat(l.prix).toFixed(2)} €</p>
                        )}
                      </div>
                      <Badge variant="outline" className="flex-shrink-0 whitespace-nowrap">{l.quantite}x</Badge>
                      
                      {/* Action buttons — always visible on mobile, hover-only on desktop */}
                      <div className="flex items-center gap-1 flex-wrap basis-full sm:basis-auto sm:opacity-0 sm:group-hover:opacity-100 sm:transition-opacity">
                        {l.statut !== "livree" && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 w-7 p-0 text-green-600 hover:text-green-700 hover:bg-green-50"
                            onClick={() => handleMarkDelivered(l.id)}
                            title="Marquer comme livré"
                          >
                            <CheckCircle className="w-4 h-4" />
                          </Button>
                        )}
                        {l.statut !== "livree" && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className={`h-7 w-7 p-0 ${l.confirmation_presence === "confirme" ? "text-blue-600 bg-blue-50" : "text-slate-400 hover:text-blue-600 hover:bg-blue-50"}`}
                            onClick={() => toggleConfirmation(l)}
                            title={l.confirmation_presence === "confirme" ? "Présence confirmée (clic pour retirer)" : "Confirmer la présence manuellement (réponse SMS)"}
                            data-testid={`confirm-btn-${l.id}`}
                          >
                            <BadgeCheck className="w-4 h-4" />
                          </Button>
                        )}
                        {l.statut !== "livree" && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 w-7 p-0 text-orange-500 hover:text-orange-600 hover:bg-orange-50"
                            onClick={() => openReport(l)}
                            title="Reporter (client absent / autre date)"
                            data-testid={`report-btn-${l.id}`}
                          >
                            <CalendarClock className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 w-7 p-0 text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50"
                          onClick={() => openEditLivraison(l)}
                          title="Modifier"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleRemoveFromTournee(l.id)}
                          title="Retirer de la tournée"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                      
                      {/* Status indicator */}
                      {l.statut === "livree" ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 px-1 text-green-600 hover:text-amber-600 hover:bg-amber-50"
                          onClick={() => handleUndoDelivery(l.id)}
                          title="Annuler la livraison (remettre en cours)"
                          data-testid={`undo-delivery-btn-${l.id}`}
                        >
                          <CheckCircle className="w-5 h-5" />
                        </Button>
                      ) : l.statut === "reportee" ? (
                        <AlertCircle className="w-5 h-5 text-orange-500 flex-shrink-0" />
                      ) : (
                        <Clock className="w-5 h-5 text-amber-500 flex-shrink-0" />
                      )}
                      
                      {/* Reorder buttons */}
                      <div className="flex flex-col gap-0.5">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-5 w-5 p-0"
                          onClick={() => idx > 0 && moveItem(idx, idx - 1)}
                          disabled={idx === 0}
                        >
                          <ChevronUp className="w-3 h-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-5 w-5 p-0"
                          onClick={() =>
                            idx < tourneeDetails.livraisons.length - 1 && moveItem(idx, idx + 1)
                          }
                          disabled={idx === tourneeDetails.livraisons.length - 1}
                        >
                          <ChevronDown className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Return to depot */}
                <div className="flex items-center gap-3 p-3 bg-indigo-100 rounded-lg mt-2">
                  <div className="w-8 h-8 rounded-full bg-indigo-600 flex items-center justify-center">
                    <Warehouse className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-indigo-800">Retour - Dépôt</p>
                    <p className="text-sm text-indigo-600">{tourneeDetails.point_depart.adresse}</p>
                  </div>
                </div>

                {/* Frais réels (essence, adblue, etc.) */}
                <div className="mt-4 pt-4 border-t space-y-2" data-testid="frais-block">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                      <Receipt className="w-4 h-4 text-amber-600" />
                      Frais réels (essence, péage…)
                    </p>
                    <Button size="sm" variant="outline" className="h-7 text-xs border-amber-200 text-amber-700 hover:bg-amber-50"
                      onClick={() => openFraisDialog(tourneeDetails.tournee.id)} data-testid="add-frais-btn">
                      <Plus className="w-3 h-3 mr-1" />Ajouter un frais
                    </Button>
                  </div>
                  {(() => {
                    const fraisArr = tourneeDetails.tournee.frais_tournee || [];
                    const labels = { essence: "Essence", adblue: "AdBlue", peage: "Péage", parking: "Parking", lavage: "Lavage", autre: "Autre" };
                    const fraisTotal = fraisArr.reduce((a, f) => a + (f.montant || 0), 0);
                    if (fraisArr.length === 0) {
                      return <p className="text-xs text-slate-400 italic">Aucun frais enregistré. Ajoute ce que tu as payé réellement (essence, AdBlue, etc.) — une ligne est créée dans les <b>Charges</b> automatiquement.</p>;
                    }
                    return (
                      <>
                        <div className="space-y-1.5">
                          {fraisArr.map(f => (
                            <div key={f.id} className="flex items-center justify-between text-xs bg-amber-50 border border-amber-100 rounded-lg px-2 py-1.5" data-testid={`frais-row-${f.id}`}>
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-amber-700">{labels[f.type] || f.type}</span>
                                <span className="font-semibold">{(f.montant || 0).toFixed(2)} €</span>
                                {f.note && <span className="text-slate-500 italic">— {f.note}</span>}
                              </div>
                              <button onClick={() => handleDeleteFrais(tourneeDetails.tournee.id, f.id)} className="text-red-400 hover:text-red-600" data-testid={`del-frais-${f.id}`}>
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                        <div className="flex items-center justify-between text-xs pt-1 border-t">
                          <span className="text-slate-500">Total frais réels :</span>
                          <span className="font-bold text-amber-700">{fraisTotal.toFixed(2)} €</span>
                        </div>
                        <p className="text-xs text-slate-400">
                          Estimation carburant auto : <b>{(tourneeDetails.tournee.cout_carburant || 0).toFixed(2)} €</b>
                        </p>
                      </>
                    );
                  })()}
                </div>

                {/* SMS en masse + Bon de tournée buttons */}
                <div className="mt-4 pt-4 border-t space-y-2">
                  <Button
                    className="w-full bg-green-600 hover:bg-green-700"
                    onClick={handleOpenSmsDialog}
                    data-testid="sms-masse-btn"
                  >
                    <MessageSquare className="w-4 h-4 mr-2" />
                    Envoyer les SMS de confirmation ({tourneeDetails.livraisons.filter(l => l.statut !== "livree").length} clients)
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                    onClick={handleBonLivraison}
                    data-testid="bon-livraison-btn"
                  >
                    <Printer className="w-4 h-4 mr-2" />
                    Imprimer le bon de tournée (PDF)
                  </Button>
                </div>

                {/* Confirmations summary */}
                {tourneeDetails.livraisons.some(l => l.confirmation_presence !== "en_attente") && (
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      Confirmations de présence
                    </p>
                    <div className="grid grid-cols-3 gap-2 mb-3">
                      <div className="bg-green-50 border border-green-200 rounded-lg p-2 text-center">
                        <p className="text-green-700 font-bold text-xl">{tourneeDetails.livraisons.filter(l => l.confirmation_presence === "confirme").length}</p>
                        <p className="text-green-600 text-xs">Confirmés</p>
                      </div>
                      <div className="bg-red-50 border border-red-200 rounded-lg p-2 text-center">
                        <p className="text-red-700 font-bold text-xl">{tourneeDetails.livraisons.filter(l => l.confirmation_presence === "absent").length}</p>
                        <p className="text-red-600 text-xs">Absents</p>
                      </div>
                      <div className="bg-orange-50 border border-orange-200 rounded-lg p-2 text-center">
                        <p className="text-orange-700 font-bold text-xl">{tourneeDetails.livraisons.filter(l => l.confirmation_presence === "en_attente" && l.statut !== "livree").length}</p>
                        <p className="text-orange-600 text-xs">En attente</p>
                      </div>
                    </div>
                    {/* List absents + pending with call button */}
                    {tourneeDetails.livraisons.filter(l => l.confirmation_presence !== "confirme" && l.statut !== "livree").map(l => (
                      <div key={l.id} className="flex items-center justify-between p-2 rounded-lg border bg-slate-50 mb-1">
                        <div>
                          <p className="font-medium text-sm text-slate-800">{l.client_nom}</p>
                          <p className="text-xs text-slate-500">{l.details_commande}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          {l.confirmation_presence === "absent" && (
                            <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">Absent</span>
                          )}
                          {l.client_telephone && (
                            <a href={`tel:${l.client_telephone}`} className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center hover:bg-indigo-200 transition-colors">
                              <Phone className="w-4 h-4 text-indigo-600" />
                            </a>
                          )}
                          <button
                            type="button"
                            onClick={() => toggleConfirmation(l)}
                            className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center hover:bg-blue-200 transition-colors"
                            title="Confirmer la présence manuellement"
                            data-testid={`confirm-sms-btn-${l.id}`}
                          >
                            <BadgeCheck className="w-4 h-4 text-blue-600" />
                          </button>
                          <button
                            type="button"
                            onClick={() => openReport(l)}
                            className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center hover:bg-orange-200 transition-colors"
                            title="Reporter cette livraison"
                            data-testid={`report-sms-btn-${l.id}`}
                          >
                            <CalendarClock className="w-4 h-4 text-orange-600" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Dialog Reporter */}
      <Dialog open={!!reportDialog} onOpenChange={(v) => !v && setReportDialog(null)}>
        <DialogContent className="max-w-md" data-testid="report-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarClock className="w-5 h-5 text-orange-500" />
              Reporter : {reportDialog?.client_nom}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label>Raison</Label>
              <Input value={reportForm.raison} onChange={(e) => setReportForm({ ...reportForm, raison: e.target.value })} placeholder="Ex: Client absent, demande de report…" data-testid="report-raison" />
            </div>
            <div>
              <Label>Note (optionnel)</Label>
              <Input value={reportForm.note} onChange={(e) => setReportForm({ ...reportForm, note: e.target.value })} placeholder="Détails…" />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox id="report_creer" checked={reportForm.creer_prochaine} onCheckedChange={(v) => setReportForm({ ...reportForm, creer_prochaine: !!v })} />
              <Label htmlFor="report_creer" className="cursor-pointer">Créer la prochaine livraison (mêmes articles, même prix)</Label>
            </div>
            {reportForm.creer_prochaine && (
              <>
                <div>
                  <Label>Placer dans une tournée</Label>
                  <Select value={reportForm.tournee_cible_id} onValueChange={(v) => setReportForm({ ...reportForm, tournee_cible_id: v })}>
                    <SelectTrigger data-testid="report-tournee-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Aucune — je déciderai plus tard</SelectItem>
                      {tournees
                        .filter((t) => (t.statut === "planifiee" || t.statut === "en_cours") && !t.is_historique && t.id !== tourneeDetails?.tournee?.id)
                        .map((t) => (
                          <SelectItem key={t.id} value={t.id}>{t.nom} — {t.date?.slice(0, 10)}</SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-400 mt-1">La nouvelle livraison prendra la date de la tournée choisie, itinéraire recalculé automatiquement.</p>
                </div>
                <div>
                  <Label>Date souhaitée (optionnel, remplace la date de la tournée)</Label>
                  <Input type="date" value={reportForm.date_souhaitee} onChange={(e) => setReportForm({ ...reportForm, date_souhaitee: e.target.value })} data-testid="report-date" />
                </div>
              </>
            )}
            <Button className="w-full bg-orange-500 hover:bg-orange-600" onClick={submitReport} disabled={reportSaving} data-testid="submit-report">
              <CalendarClock className="w-4 h-4 mr-2" />{reportSaving ? "…" : "Reporter la livraison"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Livraison Dialog */}
      <Dialog open={editLivraisonDialog} onOpenChange={setEditLivraisonDialog}>
        <DialogContent className="max-w-lg" data-testid="edit-livraison-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-indigo-600" />
              Modifier la livraison
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditLivraison} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nom du client</Label>
                <Input
                  value={editForm.client_nom}
                  onChange={(e) => setEditForm({ ...editForm, client_nom: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Téléphone</Label>
                <Input
                  value={editForm.client_telephone}
                  onChange={(e) => setEditForm({ ...editForm, client_telephone: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Adresse</Label>
              <Input
                value={editForm.adresse}
                onChange={(e) => setEditForm({ ...editForm, adresse: e.target.value })}
                required
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Code postal</Label>
                <Input
                  value={editForm.code_postal}
                  onChange={(e) => setEditForm({ ...editForm, code_postal: e.target.value })}
                />
              </div>
              <div className="space-y-2 col-span-2">
                <Label>Ville</Label>
                <Input
                  value={editForm.ville}
                  onChange={(e) => setEditForm({ ...editForm, ville: e.target.value })}
                  required
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Quantité</Label>
                <Input
                  type="number"
                  min="1"
                  value={editForm.quantite}
                  onChange={(e) => setEditForm({ ...editForm, quantite: parseInt(e.target.value) })}
                />
              </div>
              <div className="space-y-2">
                <Label>Détails commande</Label>
                <Input
                  value={editForm.details_commande}
                  onChange={(e) => setEditForm({ ...editForm, details_commande: e.target.value })}
                  placeholder="2x Chaise Milan..."
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={editForm.notes}
                onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                placeholder="Instructions spéciales..."
                rows={2}
              />
            </div>
            <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700">
              <Edit className="w-4 h-4 mr-2" />
              Enregistrer les modifications
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Livraison to Tournee Dialog */}
      <Dialog open={addLivraisonDialog} onOpenChange={(open) => {
        setAddLivraisonDialog(open);
        if (!open) {
          setAddDialogTab("livraisons");
          setClientSearchTerm("");
          setNewLivraisonFromClient({ details_commande: "", quantite: 1, prix: null, notes: "" });
        }
      }}>
        <DialogContent className="max-w-xl" data-testid="add-livraison-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-indigo-600" />
              Ajouter à la tournée
            </DialogTitle>
          </DialogHeader>
          
          {/* Tabs */}
          <div className="flex gap-2 mt-2 border-b">
            <button
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                addDialogTab === "livraisons" 
                  ? "border-indigo-600 text-indigo-600" 
                  : "border-transparent text-slate-500 hover:text-slate-700"
              }`}
              onClick={() => setAddDialogTab("livraisons")}
            >
              <Package className="w-4 h-4 inline mr-2" />
              Livraisons en attente
            </button>
            <button
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                addDialogTab === "clients" 
                  ? "border-indigo-600 text-indigo-600" 
                  : "border-transparent text-slate-500 hover:text-slate-700"
              }`}
              onClick={() => setAddDialogTab("clients")}
            >
              <Users className="w-4 h-4 inline mr-2" />
              Clients CRM
            </button>
          </div>

          <div className="mt-4">
            {/* Tab: Livraisons en attente */}
            {addDialogTab === "livraisons" && (
              <div className="border rounded-lg max-h-80 overflow-y-auto">
                {allLivraisons.filter(l => 
                  l.latitude && 
                  l.longitude && 
                  !tourneeDetails?.tournee.livraisons_ids.includes(l.id) &&
                  l.statut === "en_attente"
                ).length === 0 ? (
                  <div className="p-6 text-center">
                    <Package className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500 font-medium">Aucune livraison disponible</p>
                    <p className="text-sm text-slate-400 mt-1">
                      Utilisez l'onglet "Clients CRM" pour créer une nouvelle livraison
                    </p>
                  </div>
                ) : (
                  allLivraisons.filter(l => 
                    l.latitude && 
                    l.longitude && 
                    !tourneeDetails?.tournee.livraisons_ids.includes(l.id) &&
                    l.statut === "en_attente"
                  ).map((l) => (
                    <div
                      key={l.id}
                      className="flex items-center gap-3 p-3 border-b last:border-b-0 hover:bg-slate-50"
                    >
                      <div className="flex-1">
                        <p className="font-medium text-sm">{l.client_nom}</p>
                        <p className="text-xs text-slate-500">
                          {l.adresse}, {l.code_postal} {l.ville}
                        </p>
                      </div>
                      <Badge variant="outline" className="text-xs">
                        {l.quantite}x
                      </Badge>
                      <Button
                        size="sm"
                        onClick={() => handleAddToTournee(l.id)}
                        className="bg-indigo-600 hover:bg-indigo-700"
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Tab: Clients CRM */}
            {addDialogTab === "clients" && (
              <div className="space-y-4">
                {/* Search */}
                <div className="relative">
                  <Input
                    placeholder="Rechercher un client..."
                    value={clientSearchTerm}
                    onChange={(e) => setClientSearchTerm(e.target.value)}
                    className="pl-9"
                  />
                  <Users className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                </div>

                {/* Clients list */}
                <div className="border rounded-lg max-h-64 overflow-y-auto">
                  {clients.filter(c => 
                    c.adresse && c.ville &&
                    (clientSearchTerm === "" || 
                     c.nom.toLowerCase().includes(clientSearchTerm.toLowerCase()) ||
                     c.telephone?.includes(clientSearchTerm))
                  ).length === 0 ? (
                    <div className="p-6 text-center">
                      <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                      <p className="text-slate-500 font-medium">
                        {clients.length === 0 
                          ? "Aucun client dans le CRM" 
                          : "Aucun client avec adresse complète"}
                      </p>
                      <p className="text-sm text-slate-400 mt-1">
                        Les clients doivent avoir une adresse et une ville pour être ajoutés
                      </p>
                    </div>
                  ) : (
                    clients.filter(c => 
                      c.adresse && c.ville &&
                      (clientSearchTerm === "" || 
                       c.nom.toLowerCase().includes(clientSearchTerm.toLowerCase()) ||
                       c.telephone?.includes(clientSearchTerm))
                    ).map((c) => (
                      <div
                        key={c.id}
                        className="flex items-center gap-3 p-3 border-b last:border-b-0 hover:bg-slate-50"
                      >
                        <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                          <span className="text-indigo-600 font-semibold text-sm">
                            {c.nom.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{c.nom}</p>
                          <p className="text-xs text-slate-500 truncate">
                            {c.adresse}, {c.code_postal} {c.ville}
                          </p>
                          {c.telephone && (
                            <p className="text-xs text-slate-400">{c.telephone}</p>
                          )}
                        </div>
                        <Badge variant="outline" className="text-xs flex-shrink-0">
                          {c.nombre_commandes || 0} cmd
                        </Badge>
                        <Button
                          size="sm"
                          onClick={() => handleAddClientToTournee(c)}
                          disabled={creatingFromClient}
                          className="bg-indigo-600 hover:bg-indigo-700 flex-shrink-0"
                        >
                          {creatingFromClient ? (
                            <RefreshCw className="w-4 h-4 animate-spin" />
                          ) : (
                            <Plus className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                    ))
                  )}
                </div>

                {/* Quick options for new livraison */}
                <div className="bg-slate-50 rounded-lg p-3 space-y-3">
                  <p className="text-xs font-medium text-slate-600 uppercase tracking-wide">
                    Options pour la nouvelle livraison
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Quantité</Label>
                      <Input
                        type="number"
                        min="1"
                        value={newLivraisonFromClient.quantite}
                        onChange={(e) => setNewLivraisonFromClient({
                          ...newLivraisonFromClient,
                          quantite: parseInt(e.target.value) || 1
                        })}
                        className="h-8 text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Prix (€)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="Optionnel"
                        value={newLivraisonFromClient.prix || ""}
                        onChange={(e) => setNewLivraisonFromClient({
                          ...newLivraisonFromClient,
                          prix: e.target.value ? parseFloat(e.target.value) : null
                        })}
                        className="h-8 text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Détails commande</Label>
                    <Input
                      placeholder="Ex: 2x Milan Noir"
                      value={newLivraisonFromClient.details_commande}
                      onChange={(e) => setNewLivraisonFromClient({
                        ...newLivraisonFromClient,
                        details_commande: e.target.value
                      })}
                      className="h-8 text-sm"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* SMS en masse Dialog */}
      <Dialog open={smsDialogOpen} onOpenChange={(open) => {
        setSmsDialogOpen(open);
        if (!open) {
          setSmsType("livraison");
          setSelectedLienAvis("");
          setAvisClients([]);
          setNewAvisClient({ nom: "", telephone: "" });
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="sms-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-green-600" />
              {smsType === "livraison" ? `SMS de confirmation — ${tourneeDetails?.tournee.nom}` : "SMS Demande d'avis"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            {/* Type de SMS */}
            <div className="flex gap-2 p-1 bg-slate-100 rounded-lg">
              <button
                type="button"
                onClick={() => {
                  setSmsType("livraison");
                  setSmsMessages(buildSmsMessages(smsStartTime, smsDuration, smsDate));
                }}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  smsType === "livraison" 
                    ? "bg-white shadow text-indigo-600" 
                    : "text-slate-600 hover:text-slate-800"
                }`}
              >
                📦 SMS Livraison
              </button>
              <button
                type="button"
                onClick={() => {
                  setSmsType("avis");
                  // Initialize avisClients from tournee livraisons with default lien
                  if (tourneeDetails?.livraisons) {
                    const defaultLien = liensAvis.length > 0 ? liensAvis[0].id : "";
                    setAvisClients(tourneeDetails.livraisons.map(l => ({
                      id: l.id,
                      nom: l.client_nom,
                      telephone: l.client_telephone,
                      lien_avis_id: defaultLien,
                    })));
                  }
                }}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  smsType === "avis" 
                    ? "bg-white shadow text-indigo-600" 
                    : "text-slate-600 hover:text-slate-800"
                }`}
              >
                ⭐ SMS Demande d'avis
              </button>
            </div>

            {/* Config pour SMS livraison */}
            {smsType === "livraison" && (
              <div className="grid grid-cols-3 gap-3 p-4 bg-slate-50 rounded-lg">
                <div>
                  <label className="text-sm font-medium text-slate-700 block mb-1">📅 Date livraison</label>
                  <input
                    type="date"
                    value={smsDate}
                    onChange={(e) => {
                      setSmsDate(e.target.value);
                      setSmsMessages(buildSmsMessages(smsStartTime, smsDuration, e.target.value));
                    }}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    data-testid="sms-date"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 block mb-1">Heure de départ</label>
                  <input
                    type="time"
                    value={smsStartTime}
                    onChange={(e) => {
                      setSmsStartTime(e.target.value);
                      setSmsMessages(buildSmsMessages(e.target.value, smsDuration, smsDate));
                    }}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    data-testid="sms-start-time"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 block mb-1">Durée / arrêt (min)</label>
                  <input
                    type="number"
                    min="5"
                    max="60"
                    value={smsDuration}
                    onChange={(e) => {
                      const d = parseInt(e.target.value) || 17;
                      setSmsDuration(d);
                      setSmsMessages(buildSmsMessages(smsStartTime, d, smsDate));
                    }}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    data-testid="sms-duration"
                  />
                </div>
              </div>
            )}

            {/* Config pour SMS avis - Ajouter un client */}
            {smsType === "avis" && (
              <div className="space-y-4">
                {liensAvis.length === 0 ? (
                  <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-sm text-slate-500">
                      Aucun lien d'avis configuré.{" "}
                      <a href="/marketing" className="text-indigo-600 hover:underline">
                        Configurer dans Marketing →
                      </a>
                    </p>
                  </div>
                ) : (
                  <div className="p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
                    <p className="text-sm font-medium text-indigo-700 mb-3">Ajouter un client manuellement</p>
                    <div className="flex gap-2">
                      <Input
                        placeholder="Nom"
                        value={newAvisClient.nom}
                        onChange={(e) => setNewAvisClient({ ...newAvisClient, nom: e.target.value })}
                        className="flex-1 bg-white"
                      />
                      <Input
                        placeholder="Téléphone"
                        value={newAvisClient.telephone}
                        onChange={(e) => setNewAvisClient({ ...newAvisClient, telephone: e.target.value })}
                        className="w-36 bg-white"
                      />
                      <Button
                        size="sm"
                        onClick={() => {
                          if (newAvisClient.nom && newAvisClient.telephone) {
                            setAvisClients([...avisClients, {
                              id: `manual-${Date.now()}`,
                              nom: newAvisClient.nom,
                              telephone: newAvisClient.telephone,
                              lien_avis_id: liensAvis[0]?.id || "",
                            }]);
                            setNewAvisClient({ nom: "", telephone: "" });
                          }
                        }}
                        className="bg-indigo-600 hover:bg-indigo-700"
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            <p className="text-sm text-slate-500">
              {smsType === "livraison" 
                ? "Cliquez sur Envoyer SMS pour chaque client. Votre app SMS s'ouvrira avec le message pré-rempli."
                : `${avisClients.length} client(s) — Choisissez le lien d'avis pour chacun.`
              }
            </p>

            {/* Messages list - SMS Livraison */}
            {smsType === "livraison" && (
              <div className="space-y-3">
                {/* Récap total encaissement */}
                {smsMessages.length > 0 && (() => {
                  const confirmedIds = new Set(
                    tourneeDetails.livraisons.filter(l => l.confirmation_presence === "confirme").map(l => l.id)
                  );
                  let totalAll = 0, totalConfirmed = 0;
                  smsMessages.forEach(m => {
                    const p = (m.allLivraisons || [m.livraison]).reduce((s, l) => s + (l.prix || 0), 0);
                    totalAll += p;
                    if ((m.allLivraisons || [m.livraison]).some(l => confirmedIds.has(l.id))) totalConfirmed += p;
                  });
                  return (
                    <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg flex flex-wrap items-center justify-between gap-2 text-sm" data-testid="sms-total-encaissement">
                      <div>
                        <span className="text-slate-600">Total à encaisser (confirmés) :</span>{" "}
                        <strong className="text-emerald-700">{totalConfirmed.toFixed(2)} €</strong>
                      </div>
                      <div className="text-xs text-slate-500">
                        Total global : <strong className="text-slate-700">{totalAll.toFixed(2)} €</strong> · {smsMessages.length} client(s)
                      </div>
                    </div>
                  );
                })()}
                {smsMessages.map((item, idx) => (
                  <div key={item.livraison.id} className="border rounded-xl p-4 bg-white" data-testid={`sms-item-${idx}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <span className="w-6 h-6 rounded-full bg-indigo-600 text-white text-xs flex items-center justify-center font-bold">{idx + 1}</span>
                          <p className="font-semibold text-slate-800">{item.livraison.client_nom}</p>
                          {item.livraison.numero_commande && (
                            <span className="text-xs font-mono bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">#{item.livraison.numero_commande}</span>
                          )}
                          <span className="text-xs bg-indigo-50 text-indigo-600 px-2 py-0.5 rounded-full font-medium">{item.heureDebut} - {item.heureFin}</span>
                          {item.livraison.confirmation_presence === "confirme" && (
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">✓ Confirmé</span>
                          )}
                          {item.livraison.confirmation_presence === "absent" && (
                            <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-medium">✗ Absent</span>
                          )}
                        </div>
                        <pre className="text-xs text-slate-600 bg-slate-50 p-3 rounded-lg whitespace-pre-wrap font-sans leading-relaxed">{item.msg}</pre>
                      </div>
                      <div className="flex flex-col gap-1">
                        <Button
                          size="sm"
                          className="bg-green-600 hover:bg-green-700"
                          onClick={() => handleSendSms(item)}
                          disabled={!item.livraison.client_telephone}
                          data-testid={`send-sms-btn-${idx}`}
                        >
                          <Send className="w-3 h-3 mr-1" />
                          SMS
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                          onClick={async () => {
                            try {
                              await navigator.clipboard.writeText(item.msg);
                              toast.success("SMS copié");
                            } catch { toast.error("Impossible de copier"); }
                          }}
                          data-testid={`copy-sms-btn-${idx}`}
                        >
                          📋 Copier
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Messages list - SMS Avis avec choix par client */}
            {smsType === "avis" && liensAvis.length > 0 && (
              <div className="space-y-3">
                {avisClients.map((client, idx) => {
                  const selectedLink = liensAvis.find(l => l.id === client.lien_avis_id);
                  const prenom = client.nom.split(" ")[0];
                  const avisSms = selectedLink 
                    ? selectedLink.template_sms
                        .replace("{prenom}", prenom)
                        .replace("{lien}", selectedLink.url)
                    : `Bonjour ${prenom}, merci pour votre achat TopCocoon !`;
                  
                  return (
                    <div key={client.id} className="border rounded-xl p-4 bg-white" data-testid={`avis-item-${idx}`}>
                      <div className="flex items-start gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <span className="w-6 h-6 rounded-full bg-yellow-500 text-white text-xs flex items-center justify-center font-bold">{idx + 1}</span>
                            <p className="font-semibold text-slate-800">{client.nom}</p>
                            <span className="text-xs text-slate-500">{client.telephone}</span>
                            
                            {/* Sélecteur de lien par client */}
                            <Select 
                              value={client.lien_avis_id} 
                              onValueChange={(v) => {
                                setAvisClients(avisClients.map(c => 
                                  c.id === client.id ? { ...c, lien_avis_id: v } : c
                                ));
                              }}
                            >
                              <SelectTrigger className="w-40 h-7 text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {liensAvis.map((lien) => (
                                  <SelectItem key={lien.id} value={lien.id}>
                                    <span className="flex items-center gap-2">
                                      <span 
                                        className="w-2 h-2 rounded-full" 
                                        style={{ backgroundColor: lien.couleur }}
                                      ></span>
                                      {lien.nom}
                                    </span>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            
                            {/* Bouton supprimer */}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0 text-red-400 hover:text-red-600"
                              onClick={() => setAvisClients(avisClients.filter(c => c.id !== client.id))}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                          <pre className="text-xs text-slate-600 bg-slate-50 p-3 rounded-lg whitespace-pre-wrap font-sans leading-relaxed">{avisSms}</pre>
                        </div>
                        <Button
                          size="sm"
                          className="bg-yellow-500 hover:bg-yellow-600 flex-shrink-0"
                          onClick={() => {
                            if (client.telephone) {
                              // Log SMS
                              axios.post(`${API}/marketing/sms-envoyes`, {
                                livraison_id: client.id,
                                client_nom: client.nom,
                                client_telephone: client.telephone,
                                type_sms: "avis",
                                message: avisSms,
                                lien_avis_id: client.lien_avis_id
                              }, { headers }).catch(() => {});
                              window.location.href = `sms:${client.telephone}?body=${encodeURIComponent(avisSms)}`;
                            } else {
                              toast.error("Pas de numéro de téléphone");
                            }
                          }}
                          disabled={!client.telephone || !client.lien_avis_id}
                        >
                          <Send className="w-3 h-3 mr-1" />
                          SMS
                        </Button>
                      </div>
                    </div>
                  );
                })}
                
                {avisClients.length === 0 && (
                  <div className="text-center py-6 text-slate-500">
                    <Star className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                    <p>Aucun client à contacter</p>
                    <p className="text-sm">Ajoutez des clients avec le formulaire ci-dessus</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Tournée historique Dialog */}
      <Dialog open={historiqueDialogOpen} onOpenChange={(open) => {
        setHistoriqueDialogOpen(open);
        if (!open) {
          setHistoriqueForm({ nom: "", date: new Date(Date.now() - 86400000).toISOString().slice(0, 10), deduire_stock: false, enveloppe_montant: "", enveloppe_nb_chaises: "" });
          setHistoriqueClients([{ id: Date.now(), nom: "", telephone: "", details_commande: "", quantite: 1, prix: "" }]);
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="historique-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-amber-600" />
              Ajouter une tournée passée
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateHistorique} className="space-y-5 mt-2">
            {/* Infos de base */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nom de la tournée *</Label>
                <Input
                  value={historiqueForm.nom}
                  onChange={(e) => setHistoriqueForm({ ...historiqueForm, nom: e.target.value })}
                  placeholder="Tournée 15 Janvier 2025"
                  required
                  data-testid="historique-nom-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={historiqueForm.date}
                  onChange={(e) => setHistoriqueForm({ ...historiqueForm, date: e.target.value })}
                  max={new Date().toISOString().slice(0, 10)}
                  required
                  data-testid="historique-date-input"
                />
              </div>
            </div>

            {/* Option stock */}
            <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border">
              <Checkbox
                id="deduire_stock"
                checked={historiqueForm.deduire_stock}
                onCheckedChange={(v) => setHistoriqueForm({ ...historiqueForm, deduire_stock: !!v })}
                data-testid="historique-deduire-stock"
              />
              <div>
                <Label htmlFor="deduire_stock" className="font-medium cursor-pointer">Déduire du stock actuel</Label>
                <p className="text-xs text-slate-500 mt-0.5">Décochez si le stock a déjà été renouvelé depuis cette livraison</p>
              </div>
            </div>

            {/* Enveloppe */}
            <div className="space-y-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-sm font-semibold text-amber-700 flex items-center gap-2">
                <Wallet className="w-4 h-4" />
                Enveloppe (encaissements)
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Montant total encaissé (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="Ex: 1250.00"
                    value={historiqueForm.enveloppe_montant}
                    onChange={(e) => setHistoriqueForm({ ...historiqueForm, enveloppe_montant: e.target.value })}
                    className="h-9"
                    data-testid="historique-montant-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Nombre de chaises livrées</Label>
                  <Input
                    type="number"
                    min="0"
                    placeholder="Ex: 8"
                    value={historiqueForm.enveloppe_nb_chaises}
                    onChange={(e) => setHistoriqueForm({ ...historiqueForm, enveloppe_nb_chaises: e.target.value })}
                    className="h-9"
                    data-testid="historique-nb-chaises-input"
                  />
                </div>
              </div>
            </div>

            {/* Liste des clients */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="font-semibold">Clients visités ({historiqueClients.filter(c => c.nom).length})</Label>
                <Button type="button" size="sm" variant="outline" onClick={addHistoriqueClientRow} className="h-7 text-xs">
                  <Plus className="w-3 h-3 mr-1" />
                  Ajouter un client
                </Button>
              </div>
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-slate-50 px-3 py-2 grid grid-cols-12 gap-2 text-xs font-medium text-slate-500 border-b">
                  <span className="col-span-3">Nom *</span>
                  <span className="col-span-2">Téléphone</span>
                  <span className="col-span-3">Commande</span>
                  <span className="col-span-1">Qté</span>
                  <span className="col-span-2">Prix (€)</span>
                  <span className="col-span-1"></span>
                </div>
                <div className="max-h-52 overflow-y-auto divide-y">
                  {historiqueClients.map((client) => (
                    <div key={client.id} className="px-3 py-2 grid grid-cols-12 gap-2 items-center">
                      <div className="col-span-3">
                        <Input
                          placeholder="Nom client"
                          value={client.nom}
                          onChange={(e) => updateHistoriqueClient(client.id, "nom", e.target.value)}
                          className="h-8 text-sm"
                        />
                      </div>
                      <div className="col-span-2">
                        <Input
                          placeholder="Tél."
                          value={client.telephone}
                          onChange={(e) => updateHistoriqueClient(client.id, "telephone", e.target.value)}
                          className="h-8 text-sm"
                        />
                      </div>
                      <div className="col-span-3">
                        <Input
                          placeholder="2x Milan Noir"
                          value={client.details_commande}
                          onChange={(e) => updateHistoriqueClient(client.id, "details_commande", e.target.value)}
                          className="h-8 text-sm"
                        />
                      </div>
                      <div className="col-span-1">
                        <Input
                          type="number"
                          min="1"
                          value={client.quantite}
                          onChange={(e) => updateHistoriqueClient(client.id, "quantite", e.target.value)}
                          className="h-8 text-sm"
                        />
                      </div>
                      <div className="col-span-2">
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="0.00"
                          value={client.prix}
                          onChange={(e) => updateHistoriqueClient(client.id, "prix", e.target.value)}
                          className="h-8 text-sm"
                        />
                      </div>
                      <div className="col-span-1 flex justify-center">
                        <button
                          type="button"
                          onClick={() => removeHistoriqueClientRow(client.id)}
                          disabled={historiqueClients.length === 1}
                          className="text-red-400 hover:text-red-600 disabled:opacity-30"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-amber-600 hover:bg-amber-700"
              disabled={!historiqueForm.nom || !historiqueForm.date}
              data-testid="submit-historique-btn"
            >
              <History className="w-4 h-4 mr-2" />
              Enregistrer la tournée passée
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Enveloppe Edit Dialog */}
      <EnveloppeDialog
        open={enveloppeDialogOpen}
        onOpenChange={setEnveloppeDialogOpen}
        form={enveloppeForm}
        setForm={setEnveloppeForm}
        onSubmit={handleUpdateEnveloppe}
      />

      {/* Split tournée Dialog */}
      <Dialog open={splitDialogOpen} onOpenChange={setSplitDialogOpen}>
        <DialogContent className="max-w-md" data-testid="split-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">🪓 Splitter cette tournée</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {tourneeDetails?.livraisons && (() => {
              const total = tourneeDetails.livraisons.reduce((s, l) => {
                const pl = l.produits_lies || [];
                return s + (pl.length ? pl.reduce((ss, p) => ss + (parseInt(p.quantite) || 0), 0) : (parseInt(l.quantite) || 0));
              }, 0);
              const nb = Math.max(2, Math.ceil(total / (splitCap || 100)));
              return (
                <div className="p-3 bg-orange-50 rounded text-sm text-orange-900">
                  Total : <strong>{total}</strong> chaises · Capacité : <strong>{splitCap}</strong>/tournée → <strong>{nb}</strong> tournée(s) créée(s)
                </div>
              );
            })()}
            <div>
              <Label>Capacité max par tournée (chaises)</Label>
              <Input type="number" min="1" value={splitCap} onChange={(e) => setSplitCap(parseInt(e.target.value) || 100)} data-testid="split-cap-input" />
            </div>
            <div>
              <Label>Stratégie de découpe</Label>
              <Select value={splitStrategie} onValueChange={setSplitStrategie}>
                <SelectTrigger data-testid="split-strategie-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="geo">🗺️ Par proximité géographique (recommandé)</SelectItem>
                  <SelectItem value="equilibre">⚖️ Équilibrage strict (par nombre de chaises)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox id="split-del" checked={splitDeleteOriginal} onCheckedChange={(v) => setSplitDeleteOriginal(!!v)} data-testid="split-delete-check" />
              <Label htmlFor="split-del" className="cursor-pointer text-sm">Supprimer la tournée d&apos;origine (sinon elle sera annulée et vidée)</Label>
            </div>
            <p className="text-xs text-slate-500">Chaque nouvelle tournée sera datée à J, J+1, J+2… à partir de la date actuelle de la tournée. Tu pourras ajuster les dates ensuite.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSplitDialogOpen(false)} disabled={splitting}>Annuler</Button>
            <Button onClick={submitSplit} disabled={splitting} className="bg-orange-600 hover:bg-orange-700" data-testid="split-submit-btn">
              {splitting ? "Splitting…" : "Splitter la tournée"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Chargement confirmation dialog */}
      <ChargementDialog
        open={chargementDialogOpen}
        onOpenChange={setChargementDialogOpen}
        tourneeDetails={tourneeDetails}
        pendingStatutChange={pendingStatutChange}
        setPendingStatutChange={setPendingStatutChange}
        onDownloadPdf={handleDownloadChargementPdf}
        onConfirmStart={confirmStartTournee}
      />

      {/* ===== Dialog Ajout de frais (essence, adblue, etc.) ===== */}
      <FraisDialog
        open={fraisDialogOpen}
        onOpenChange={setFraisDialogOpen}
        form={fraisForm}
        setForm={setFraisForm}
        submitting={fraisSubmitting}
        onSubmit={handleAddFrais}
      />

      {/* ===== Dialog Fusion de tournées ===== */}
      <MergeDialog
        open={mergeDialogOpen}
        onOpenChange={setMergeDialogOpen}
        selectedIds={selectedTourneesForMerge}
        tournees={tournees}
        primaryId={mergePrimaryId}
        setPrimaryId={setMergePrimaryId}
        date={mergeDate}
        setDate={setMergeDate}
        submitting={mergeSubmitting}
        onConfirm={handleMerge}
      />
    </div>
  );
};

export default Tournees;
